import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useAppData } from '../context/AppDataContext';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { ChevronDown, ChevronUp } from 'lucide-react';

import MessageForm from '../components/MessageForm';
import MessageLog from '../components/MessageLog';
import '../styles/ClientProfile.css';
import '../styles/MessageLog.css';
import AnalyticsTab from "../components/AnalyticsTab";

// Shared ad constants- edit src/constants/facebookAdConstants.js to add templates or ad types
import { VERBIAGE_TEMPLATES, AVAILABLE_AD_TYPES, toBoldUnicode, toSuperscriptUnicode } from '../constants/facebookAdConstants';
import FbDisclaimerBuilder from '../components/FbDisclaimerBuilder';
import { FB_GENERIC_DISCLAIMER } from '../utils/ftcCompliance';
import { generateEventVoucher, generate500Voucher } from '../utils/voucherGenerator';


// Available Profile Picture types (can be different from ads)
const AVAILABLE_PROFILE_TYPES = {
  '2025': {
    'January': [],
    'February': [],
    'March': [],
    'April': [],
    'May': [],
    'June': [],
    'July': [],
    'August': [],
    'September': [],
    'October': [],
    'November': [],
    'December': ['ChristmasWreath', 'SpecialEventsNewYear']
  },
  '2026': {
    'January': [],
    'February': ['SpecialEventsValentines', 'FlagBorderPatriotic', 'PatrioticSpecialEvents', 'WinterSpecialEvents'],
    'March': ['SpringSpecialEvents', 'BlowoutSpecialEvents'],
    'April': [],
    'May': ['SpecialEventsPatrioticFireworks'],
    'June': ['SummerSpecialEvents'],
    'July': [],
    'August': ['SpecialEventsSchoolChalkboard', 'BackToSchoolGridStickers', 'NissanEndSummer'],
    'September': [],
    'October': ['SpecialEventsFall'],
    'November': [],
    'December': []
  }
};

// Available Header types (can be different from ads and profiles)
const AVAILABLE_HEADER_TYPES = {
 '2025': {
    'January': [],
    'February': [],
    'March': [],
    'April': [],
    'May': [],
    'June': [],
    'July': [],
    'August': [],
    'September': [],
    'October': [],
    'November': [],
    'December': ['WinterSavings', 'NewYearFireworks']
  },
  '2026': {
    'January': ['NewYearNewRide'],
    'February': ['LoveFirstDrive', 'PatrioticPresidentsDay', 'MassiveLiquidationWinter'],
    'March': ['SpringIntoSavings', 'BlowoutEvent'],
    'April': ['UpsideDownSpring'],
    'May': ['PatrioticExtravaganza'],
    'June': ['SuperSummerBlowout'],
    'July': ['Happy250th'],
    'August': ['Back2SchoolNeon', 'Back2SchoolSavings', 'BackToSchoolPaper', 'NissanCorporateBlowoutDeals'],
    'September': [],
    'October': [],
    'November': [],
    'December': []
  }
};

if (!CanvasRenderingContext2D.prototype.roundRect) {
  CanvasRenderingContext2D.prototype.roundRect = function(x, y, width, height, radius) {
    this.beginPath();
    this.moveTo(x + radius, y);
    this.lineTo(x + width - radius, y);
    this.quadraticCurveTo(x + width, y, x + width, y + radius);
    this.lineTo(x + width, y + height - radius);
    this.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    this.lineTo(x + radius, y + height);
    this.quadraticCurveTo(x, y + height, x, y + height - radius);
    this.lineTo(x, y + radius);
    this.quadraticCurveTo(x, y, x + radius, y);
    this.closePath();
  };
}



// Function to measure text and auto-size if needed
const measureAndPositionText = (ctx, text, textConfig) => {
  const { box, fontSize, fontFamily, fontWeight, autoSize, minFontSize, maxFontSize, maxLines } = textConfig;
  
  if (!autoSize) {
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    return { fontSize, lines: text.split('\n') };
  }

  // Auto-sizing logic
  let bestFontSize = fontSize;
  let bestLines = [];
  
  for (let testSize = maxFontSize; testSize >= minFontSize; testSize -= 2) {
    ctx.font = `${fontWeight} ${testSize}px ${fontFamily}`;
    
    // First split by explicit newlines
    const segments = text.split('\n');
    const lines = [];
    
    // Process each segment with word wrapping
    for (const segment of segments) {
      const words = segment.split(' ');
      let currentLine = '';
      
      for (const word of words) {
        const testLine = currentLine + (currentLine ? ' ' : '') + word;
        const metrics = ctx.measureText(testLine);
        
        if (metrics.width > box.width && currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }
      if (currentLine) lines.push(currentLine);
    }
    
    const totalHeight = lines.length * testSize * 1.2; // lineHeight
    
    if (lines.length <= maxLines && totalHeight <= box.height) {
      bestFontSize = testSize;
      bestLines = lines;
      break;
    }
  }
  
  return { fontSize: bestFontSize, lines: bestLines };
};

// Function to draw text on canvas
const drawTextElement = (ctx, text, textConfig) => {
  if (!textConfig || !textConfig.enabled || !text) {
    console.log('[DEBUG] Skipping text element:', { 
      hasConfig: !!textConfig, 
      enabled: textConfig?.enabled, 
      hasText: !!text 
    });
    return;
  }
  
  console.log('[DEBUG] Drawing text element:', { text, config: textConfig });
  
  const { 
    box, 
    color = '#000000', 
    textAlign = 'center', 
    lineHeight = 1.2, 
    textShadow, 
    backgroundColor, 
    borderRadius = 0, 
    padding = 0 
  } = textConfig;
  
  // Draw background if specified
  if (backgroundColor) {
    ctx.fillStyle = backgroundColor;
    if (borderRadius > 0) {
      // Use our polyfill
      ctx.roundRect(
        box.x - padding, 
        box.y - padding, 
        box.width + (padding * 2), 
        box.height + (padding * 2), 
        borderRadius
      );
      ctx.fill();
    } else {
      ctx.fillRect(
        box.x - padding, 
        box.y - padding, 
        box.width + (padding * 2), 
        box.height + (padding * 2)
      );
    }
  }
  
  // Measure and position text
  const { fontSize, lines } = measureAndPositionText(ctx, text, textConfig);
  
  // Set text properties
  ctx.font = `${textConfig.fontWeight || 'normal'} ${fontSize}px ${textConfig.fontFamily || 'Arial'}`;
  ctx.fillStyle = color;
  ctx.textAlign = textAlign;
  
  // Apply text shadow if specified
  if (textShadow) {
    const shadowParts = textShadow.match(/(\d+)px\s+(\d+)px\s+(\d+)px\s+(.+)/);
    if (shadowParts) {
      ctx.shadowOffsetX = parseInt(shadowParts[1]);
      ctx.shadowOffsetY = parseInt(shadowParts[2]);
      ctx.shadowBlur = parseInt(shadowParts[3]);
      ctx.shadowColor = shadowParts[4];
    }
  }
  
  // Calculate starting Y position to center the text block
  const totalTextHeight = lines.length * fontSize * lineHeight;
  const startY = box.y + (box.height - totalTextHeight) / 2 + fontSize;
  
  // Draw each line
  lines.forEach((line, index) => {
    const x = textAlign === 'center' ? box.x + box.width / 2 :
             textAlign === 'right' ? box.x + box.width :
             box.x;
    const y = startY + (index * fontSize * lineHeight);
    
    ctx.fillText(line, x, y);
  });
  
  // Reset shadow
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.shadowBlur = 0;
  ctx.shadowColor = 'transparent';
};



const positionLogoInBox = (logo, placement) => {
  const { box, objectFit, padding } = placement;
  const horizontalAlign = placement.horizontalAlign || 'center';
  const verticalAlign = placement.verticalAlign || 'center';
  
  const availableWidth = box.width - (padding * 2);
  const availableHeight = box.height - (padding * 2);
  
  const logoRatio = logo.width / logo.height;
  let logoWidth, logoHeight;
  
  if (objectFit === 'contain') {
    // Scale logo to fit within the box while maintaining aspect ratio
    if (logoRatio > (availableWidth / availableHeight)) {
      // Logo is wider relative to box - constrain by width
      logoWidth = availableWidth;
      logoHeight = availableWidth / logoRatio;
    } else {
      // Logo is taller relative to box - constrain by height
      logoHeight = availableHeight;
      logoWidth = availableHeight * logoRatio;
    }
  } else if (objectFit === 'fill') {
    // Stretch logo to fill the entire box
    logoWidth = availableWidth;
    logoHeight = availableHeight;
  } else if (objectFit === 'cover') {
    // Scale logo to cover the entire box while maintaining aspect ratio
    if (logoRatio > (availableWidth / availableHeight)) {
      logoHeight = availableHeight;
      logoWidth = availableHeight * logoRatio;
    } else {
      logoWidth = availableWidth;
      logoHeight = availableWidth / logoRatio;
    }
  }
  
  // Calculate horizontal position based on alignment
  let logoX;
  switch (horizontalAlign) {
    case 'left':
      logoX = box.x + padding;
      break;
    case 'right':
      logoX = box.x + padding + (availableWidth - logoWidth);
      break;
    case 'center':
    default:
      logoX = box.x + padding + (availableWidth - logoWidth) / 2;
      break;
  }
  
  // Calculate vertical position based on alignment
  let logoY;
  switch (verticalAlign) {
    case 'top':
      logoY = box.y + padding;
      break;
    case 'bottom':
      logoY = box.y + padding + (availableHeight - logoHeight);
      break;
    case 'center':
    default:
      logoY = box.y + padding + (availableHeight - logoHeight) / 2;
      break;
  }
  
  return {
    x: logoX,
    y: logoY,
    width: logoWidth,
    height: logoHeight
  };
};



  // Templates from shared constants
  const TEMPLATES = VERBIAGE_TEMPLATES;

// Pre-generate time options for hours dropdowns
const timeOptions = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const minutes = i % 2 === 0 ? 0 : 30;
  return new Date(0, 0, 0, hour, minutes).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
});

const daysOfWeek = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

const defaultHours = daysOfWeek.reduce((acc, day) => {
  const key = day.toLowerCase();
  acc[key] = { open: '', close: '' };
  return acc;
}, {});

function CopyButton({ text }) {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  return (
    <button
      onClick={handleCopy}
      title="Copy to clipboard"
      style={{
        marginLeft: 6,
        padding: '1px 7px',
        fontSize: 11,
        backgroundColor: copied ? '#28a745' : '#e9ecef',
        color: copied ? 'white' : '#555',
        border: '1px solid ' + (copied ? '#28a745' : '#ccc'),
        borderRadius: 4,
        cursor: 'pointer',
        verticalAlign: 'middle',
        transition: 'all 0.15s ease',
        lineHeight: '18px'
      }}
    >
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  );
}

function ClientProfile() {
  const { id } = useParams();
  const { refresh } = useAppData();

  // Clear log counters
  const [clearClientLog,   setClearClientLog]   = useState(0);
  const [clearInternalLog, setClearInternalLog] = useState(0);

  const [searchParams] = useSearchParams();
  const startInEdit    = searchParams.get('edit') === 'true';

  const [client, setClient] = useState(null);
  const [orders, setOrders] = useState([]);
  const [orderProofCache, setOrderProofCache] = useState({});
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(startInEdit);
  const [saving, setSaving] = useState(false);
  const [assetNote, setAssetNote] = useState('');

  const [facebookAdsModal, setFacebookAdsModal] = useState({
  open: false,
  imageUrl: '',
  title: '',
  adName: '',
  year: '',
  month: '',
  adType: ''
});
const [generatingFacebookAd, setGeneratingFacebookAd] = useState(false);
const [preSaleEmailModal, setPreSaleEmailModal] = useState({
  open: false,
  eventId: ''
});
const [selectedYear, setSelectedYear] = useState('');
const [selectedMonth, setSelectedMonth] = useState('');
const [selectedAdType, setSelectedAdType] = useState('');
const [availableAdTypes, setAvailableAdTypes] = useState([]);
const [loadingAdTypes, setLoadingAdTypes] = useState(false);
// eslint-disable-next-line
const adName = [selectedAdType, selectedMonth, selectedYear].filter(Boolean).join(' - ');

const [searchQuery, setSearchQuery] = useState('');
const [allAvailableAds, setAllAvailableAds] = useState([]);
const [filteredAds, setFilteredAds] = useState([]);

// Profile Pictures state
const [profilePictureModal, setProfilePictureModal] = useState({
  open: false,
  imageUrl: '',
  title: '',
  adName: '',
  year: '',
  month: '',
  adType: ''
});
const [generatingProfilePicture, setGeneratingProfilePicture] = useState(false);
const [selectedProfileYear, setSelectedProfileYear] = useState('');
const [selectedProfileMonth, setSelectedProfileMonth] = useState('');
const [selectedProfileType, setSelectedProfileType] = useState('');
const [availableProfileTypes, setAvailableProfileTypes] = useState([]);
const [loadingProfileTypes, setLoadingProfileTypes] = useState(false);
const [searchProfileQuery, setSearchProfileQuery] = useState('');
const [allAvailableProfiles, setAllAvailableProfiles] = useState([]);
const [filteredProfiles, setFilteredProfiles] = useState([]);

// Headers state
const [headerModal, setHeaderModal] = useState({
  open: false,
  imageUrl: '',
  title: '',
  adName: '',
  year: '',
  month: '',
  adType: ''
});
const [generatingHeader, setGeneratingHeader] = useState(false);
const [selectedHeaderYear, setSelectedHeaderYear] = useState('');
const [selectedHeaderMonth, setSelectedHeaderMonth] = useState('');
const [selectedHeaderType, setSelectedHeaderType] = useState('');
const [availableHeaderTypes, setAvailableHeaderTypes] = useState([]);
const [loadingHeaderTypes, setLoadingHeaderTypes] = useState(false);
const [searchHeaderQuery, setSearchHeaderQuery] = useState('');
const [allAvailableHeaders, setAllAvailableHeaders] = useState([]);
const [filteredHeaders, setFilteredHeaders] = useState([]);
  
  // Dropdown states
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Verbiage generation state
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [generatingVerbiage, setGeneratingVerbiage] = useState(false);
  const [verbiageModal, setVerbiageModal] = useState({ 
  open: false, 
  content: '', 
  title: '',
  templateName: '',
  isEditing: false,
  originalContent: '',
  rawTemplateContent: '' // Store the raw template before replacements
});
  const [disclaimerModal, setDisclaimerModal] = useState({
  open: false,
  imageUrl: '',
  title: '',
  type: '' // 'generic' or 'special'
});
const [generatingDisclaimer, setGeneratingDisclaimer] = useState(false);
const [fbBuilderOpen, setFbBuilderOpen] = useState(false);

const [customTemplateModal, setCustomTemplateModal] = useState({
  open: false,
  templateName: '',
  content: '',
  isEditing: false,
  originalTemplateName: null
});
const [savingCustomTemplate, setSavingCustomTemplate] = useState(false);

// Generate available years (current year and next few years)
const getAvailableYears = () => {
  const startYear = 2025;
  const endYear = 2026;
  const years = [];
  for (let i = startYear; i <= endYear; i++) {
    years.push(i.toString());
  }
  return years;
};

// Add this useEffect to load available ad types when year/month changes
useEffect(() => {
  if (selectedYear && selectedMonth) {
    loadAvailableAdTypes(selectedYear, selectedMonth);
  } else {
    setAvailableAdTypes([]);
    setSelectedAdType('');
  }
}, [selectedYear, selectedMonth]);

// Reset month when year changes
useEffect(() => {
  if (selectedYear) {
    setSelectedMonth('');
    setSelectedAdType('');
  }
}, [selectedYear]);

// Profile Pictures useEffects
useEffect(() => {
  if (selectedProfileYear && selectedProfileMonth) {
    loadAvailableProfileTypes(selectedProfileYear, selectedProfileMonth);
  } else {
    setAvailableProfileTypes([]);
    setSelectedProfileType('');
  }
}, [selectedProfileYear, selectedProfileMonth]);

useEffect(() => {
  if (selectedProfileYear) {
    setSelectedProfileMonth('');
    setSelectedProfileType('');
  }
}, [selectedProfileYear]);

// Headers useEffects
useEffect(() => {
  if (selectedHeaderYear && selectedHeaderMonth) {
    loadAvailableHeaderTypes(selectedHeaderYear, selectedHeaderMonth);
  } else {
    setAvailableHeaderTypes([]);
    setSelectedHeaderType('');
  }
}, [selectedHeaderYear, selectedHeaderMonth]);

useEffect(() => {
  if (selectedHeaderYear) {
    setSelectedHeaderMonth('');
    setSelectedHeaderType('');
  }
}, [selectedHeaderYear]);




const loadAllAvailableAds = useCallback(() => {
  const allAds = [];
  
  // Loop through all years and months to build complete ad list
  Object.entries(AVAILABLE_AD_TYPES).forEach(([year, yearData]) => {
    Object.entries(yearData).forEach(([month, adTypes]) => {
      adTypes.forEach(adType => {
        allAds.push({
          name: adType,
          displayName: adType
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/([a-zA-Z])(\d)/g, '$1 $2')
  .replace(/(\d)([a-zA-Z])/g, '$1 $2')
  .trim(),
          year,
          month,
          fullPath: `${year}/${month}/${adType}`
        });
      });
    });
  });
  
  setAllAvailableAds(allAds);
  setFilteredAds(allAds);
}, []);

// Load all profile pictures
const loadAllAvailableProfiles = useCallback(() => {
  const allProfiles = [];
  
  Object.entries(AVAILABLE_PROFILE_TYPES).forEach(([year, yearData]) => {
    Object.entries(yearData).forEach(([month, profileTypes]) => {
      profileTypes.forEach(profileType => {
        allProfiles.push({
          name: profileType,
          displayName: profileType
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/([a-zA-Z])(\d)/g, '$1 $2')
            .replace(/(\d)([a-zA-Z])/g, '$1 $2')
            .trim(),
          year,
          month,
          fullPath: `${year}/${month}/${profileType}`
        });
      });
    });
  });
  
  setAllAvailableProfiles(allProfiles);
  setFilteredProfiles(allProfiles);
}, []);

// Load all headers
const loadAllAvailableHeaders = useCallback(() => {
  const allHeaders = [];
  
  Object.entries(AVAILABLE_HEADER_TYPES).forEach(([year, yearData]) => {
    Object.entries(yearData).forEach(([month, headerTypes]) => {
      headerTypes.forEach(headerType => {
        allHeaders.push({
          name: headerType,
          displayName: headerType
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/([a-zA-Z])(\d)/g, '$1 $2')
            .replace(/(\d)([a-zA-Z])/g, '$1 $2')
            .trim(),
          year,
          month,
          fullPath: `${year}/${month}/${headerType}`
        });
      });
    });
  });
  
  setAllAvailableHeaders(allHeaders);
  setFilteredHeaders(allHeaders);
}, []);

// Add search filtering effect
useEffect(() => {
  if (!searchQuery.trim()) {
    setFilteredAds(allAvailableAds);
  } else {
    const filtered = allAvailableAds.filter(ad => 
      ad.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ad.month.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ad.year.includes(searchQuery)
    );
    setFilteredAds(filtered);
  }
}, [searchQuery, allAvailableAds]);

// Load all ads on component mount
useEffect(() => {
  loadAllAvailableAds();
}, [loadAllAvailableAds]);

// Profile Pictures search filtering
useEffect(() => {
  if (!searchProfileQuery.trim()) {
    setFilteredProfiles(allAvailableProfiles);
  } else {
    const filtered = allAvailableProfiles.filter(profile => 
      profile.displayName.toLowerCase().includes(searchProfileQuery.toLowerCase()) ||
      profile.month.toLowerCase().includes(searchProfileQuery.toLowerCase()) ||
      profile.year.includes(searchProfileQuery)
    );
    setFilteredProfiles(filtered);
  }
}, [searchProfileQuery, allAvailableProfiles]);

// Load all profiles on component mount
useEffect(() => {
  loadAllAvailableProfiles();
}, [loadAllAvailableProfiles]);

// Headers search filtering
useEffect(() => {
  if (!searchHeaderQuery.trim()) {
    setFilteredHeaders(allAvailableHeaders);
  } else {
    const filtered = allAvailableHeaders.filter(header => 
      header.displayName.toLowerCase().includes(searchHeaderQuery.toLowerCase()) ||
      header.month.toLowerCase().includes(searchHeaderQuery.toLowerCase()) ||
      header.year.includes(searchHeaderQuery)
    );
    setFilteredHeaders(filtered);
  }
}, [searchHeaderQuery, allAvailableHeaders]);

// Load all headers on component mount
useEffect(() => {
  loadAllAvailableHeaders();
}, [loadAllAvailableHeaders]);

const handleAdSelection = (ad) => {
  setSelectedYear(ad.year);
  setSelectedMonth(ad.month);
  setSelectedAdType(ad.name);
  
  // Delay clearing the search to allow the state updates to complete
  setTimeout(() => {
    setSearchQuery('');
  }, 1000);
};

const handleProfileSelection = (profile) => {
  setSelectedProfileYear(profile.year);
  setSelectedProfileMonth(profile.month);
  setSelectedProfileType(profile.name);
  
  setTimeout(() => {
    setSearchProfileQuery('');
  }, 1000);
};

const handleHeaderSelection = (header) => {
  setSelectedHeaderYear(header.year);
  setSelectedHeaderMonth(header.month);
  setSelectedHeaderType(header.name);
  
  setTimeout(() => {
    setSearchHeaderQuery('');
  }, 1000);
};


// Function to load available ad types for the selected year/month
const loadAvailableAdTypes = async (year, month) => {
  setLoadingAdTypes(true);
  
  try {
    const yearData = AVAILABLE_AD_TYPES[year] || {};
    const monthAdTypes = yearData[month] || [];
    
    // Instead of testing each template file, just show available ad types
    const validAdTypes = monthAdTypes.map(adType => ({
  name: adType,
  displayName: adType
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([a-zA-Z])(\d)/g, '$1 $2')
    .replace(/(\d)([a-zA-Z])/g, '$1 $2')
    .trim()
}));
    
    setAvailableAdTypes(validAdTypes);
    
  } catch (error) {
    console.error('Error loading ad types:', error);
    setAvailableAdTypes([]);
  } finally {
    setLoadingAdTypes(false);
  }
};

// Function to load available profile types
const loadAvailableProfileTypes = async (year, month) => {
  setLoadingProfileTypes(true);
  
  try {
    const yearData = AVAILABLE_PROFILE_TYPES[year] || {};
    const monthProfileTypes = yearData[month] || [];
    
    const validProfileTypes = monthProfileTypes.map(profileType => ({
      name: profileType,
      displayName: profileType
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([a-zA-Z])(\d)/g, '$1 $2')
        .replace(/(\d)([a-zA-Z])/g, '$1 $2')
        .trim()
    }));
    
    setAvailableProfileTypes(validProfileTypes);
    
  } catch (error) {
    console.error('Error loading profile types:', error);
    setAvailableProfileTypes([]);
  } finally {
    setLoadingProfileTypes(false);
  }
};

// Function to load available header types
const loadAvailableHeaderTypes = async (year, month) => {
  setLoadingHeaderTypes(true);
  
  try {
    const yearData = AVAILABLE_HEADER_TYPES[year] || {};
    const monthHeaderTypes = yearData[month] || [];
    
    const validHeaderTypes = monthHeaderTypes.map(headerType => ({
      name: headerType,
      displayName: headerType
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([a-zA-Z])(\d)/g, '$1 $2')
        .replace(/(\d)([a-zA-Z])/g, '$1 $2')
        .trim()
    }));
    
    setAvailableHeaderTypes(validHeaderTypes);
    
  } catch (error) {
    console.error('Error loading header types:', error);
    setAvailableHeaderTypes([]);
  } finally {
    setLoadingHeaderTypes(false);
  }
};




const uploadDisclaimerToFirebase = async (dataUrl, type) => {
  try {
    // Convert base64 to blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    
    // Create a unique filename with timestamp
    const timestamp = new Date().getTime();
    // eslint-disable-next-line
    const cleanClientName = client.name.replace(/[^a-zA-Z0-9]/g, '_');
    const filename = `disclaimers/${id}/${type}_disclaimer_${timestamp}.png`;
    
    // Upload to Firebase Storage
    const storageRef = ref(storage, filename);
    const snapshot = await uploadBytes(storageRef, blob);
    
    // Get the download URL
    const downloadUrl = await getDownloadURL(snapshot.ref);
    
    return downloadUrl;
  } catch (error) {
    console.error('Error uploading to Firebase Storage:', error);
    throw error;
  }
};

const createBitlyLink = async (longUrl) => {
  try {
    // Call your Firebase Function instead of Bitly directly
    const response = await fetch(
      'https://us-central1-srmg-order-system.cloudfunctions.net/createBitlyLink',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ longUrl })
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create Bitly link');
    }
    
    const data = await response.json();
    return data.link;
  } catch (error) {
    console.error('Error creating Bitly link:', error);
    throw error;
  }
};
  // Bold unicode converter (from your Python script)
// Fixed Bold unicode converter - but may not display properly in all contexts
// toBoldUnicode imported from facebookAdConstants

// toSuperscriptUnicode imported from facebookAdConstants

const generateDisclaimer = async (type, disclaimerText) => {
  if (!client || !disclaimerText.trim()) {
    alert(`Please enter ${type} disclaimer text first`);
    return;
  }
  
  setGeneratingDisclaimer(true);
  
  try {
    if (!client.name) {
      throw new Error('Client name is required');
    }
    
    const cleanedName = (client?.name || '')
      .replace(/\n|\r/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const street = client.street1?.trim() || '';
    const cityStateZip = `${client.city?.trim() || ''}, ${client.state?.trim() || ''} ${client.zip?.trim() || ''}`.trim();

    const scale = 2;
    const canvas = document.createElement('canvas');
    canvas.width = 600 * scale;
    canvas.height = 600 * scale;
    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);

    // Define safe zones for logo placement
    const LOGO_SAFE_ZONE = {
      x: 50,           // Left boundary
      y: 40,           // Top boundary (below "Event Disclaimer" text)
      width: 500,      // Available width (600 - 50px margins on each side)
      height: 100,     // Maximum height to avoid overlapping with text below
      maxWidth: 250,   // Preferred max width for logo
      maxHeight: 55   // Preferred max height for logo
    };

    function fitLogoToSafeZone(logoWidth, logoHeight) {
      const logoRatio = logoWidth / logoHeight;
      let finalWidth = LOGO_SAFE_ZONE.maxWidth;
      let finalHeight = LOGO_SAFE_ZONE.maxWidth / logoRatio;
      
      // If height exceeds max, scale down based on height
      if (finalHeight > LOGO_SAFE_ZONE.maxHeight) {
        finalHeight = LOGO_SAFE_ZONE.maxHeight;
        finalWidth = LOGO_SAFE_ZONE.maxHeight * logoRatio;
      }
      
      // Double-check we're within absolute boundaries
      if (finalWidth > LOGO_SAFE_ZONE.width) {
        finalWidth = LOGO_SAFE_ZONE.width;
        finalHeight = finalWidth / logoRatio;
      }
      
      if (finalHeight > LOGO_SAFE_ZONE.height) {
        finalHeight = LOGO_SAFE_ZONE.height;
        finalWidth = finalHeight * logoRatio;
      }
      
      // Center horizontally within safe zone
      const logoX = (600 - finalWidth) / 2;
      
      // For smaller logos, add a small vertical offset instead of full centering
      // This keeps them near the top but not right at the edge
      let logoY;
      if (finalHeight < LOGO_SAFE_ZONE.maxHeight * 0.8) {
        // Add a small offset (15px) for smaller logos
        logoY = LOGO_SAFE_ZONE.y + 15;
      } else {
        // Larger logos stay at the top
        logoY = LOGO_SAFE_ZONE.y;
      }
      
      return {
        x: logoX,
        y: logoY,
        width: finalWidth,
        height: finalHeight,
        bottomY: logoY + finalHeight
      };
    }

    function addDealerInfoWithoutLogo(ctx, name, street, cityStateZip) {
      ctx.fillStyle = 'black';
      ctx.font = 'bold 24px "Times New Roman", serif';
      ctx.textAlign = 'center';
      ctx.fillText(name, 300, 540);
      
      const fullAddress = `${street}${street ? ', ' : ''}${cityStateZip}`.trim();
      if (fullAddress) {
        ctx.font = 'bold 16px "Times New Roman", serif';
        ctx.fillText(fullAddress, 300, 558);
      }
    }

    function addDisclaimerText(ctx, text, startY) {
      ctx.fillStyle = 'black';
      ctx.textAlign = 'center';
      
      const upperText = text.toUpperCase();
      const maxWidth = 520;
      const words = upperText.split(' ');
      
      // Define boundaries with proper constraints
      const minY = startY + 5; // Minimum top boundary (reduced buffer for larger text)
      const maxY = 500; // Maximum bottom boundary (40px above dealer info)
      const availableHeight = maxY - minY;
      
      // Ensure we have enough space to work with
      if (availableHeight < 50) {
        console.warn('Not enough space for disclaimer text');
        return;
      }
      
      // Calculate text metrics for a given font size
      function getTextMetrics(fontSize) {
        ctx.font = `900 ${fontSize}px Arial, sans-serif`;
        const lineHeight = fontSize * 1.1;
        const lines = [];
        let currentLine = '';
        
        for (let i = 0; i < words.length; i++) {
          const testLine = currentLine + words[i] + ' ';
          if (ctx.measureText(testLine).width > maxWidth && currentLine !== '') {
            lines.push(currentLine.trim());
            currentLine = words[i] + ' ';
          } else {
            currentLine = testLine;
          }
        }
        if (currentLine.trim()) lines.push(currentLine.trim());
        
        const totalTextHeight = lines.length * lineHeight;
        return { lines, lineHeight, totalTextHeight, fontSize };
      }
      
      // Binary search for optimal font size
      let minFont = 8;
      let maxFont = Math.min(140, Math.floor(availableHeight / 2)); // Increased max from 120 to 140
      let bestMetrics = null;
      
      while (minFont <= maxFont) {
        const midFont = Math.floor((minFont + maxFont) / 2);
        const metrics = getTextMetrics(midFont);
        
        if (metrics.totalTextHeight <= availableHeight) {
          bestMetrics = metrics;
          minFont = midFont + 1;
        } else {
          maxFont = midFont - 1;
        }
      }
      
      if (!bestMetrics) {
        bestMetrics = getTextMetrics(8);
      }
      
      ctx.font = `900 ${bestMetrics.fontSize}px Arial, sans-serif`;
      
      // Calculate optimal vertical positioning
      let textStartY;
      
      if (bestMetrics.totalTextHeight >= availableHeight * 0.8) {
        textStartY = minY + bestMetrics.fontSize + 10;
      } else if (bestMetrics.totalTextHeight <= availableHeight * 0.3) {
        const topBias = availableHeight * 0.08; // Reduced from 0.15 to 0.08 for better space utilization
        textStartY = minY + topBias + bestMetrics.fontSize;
      } else {
        const unusedSpace = availableHeight - bestMetrics.totalTextHeight;
        const topMargin = unusedSpace * 0.35;
        textStartY = minY + topMargin + bestMetrics.fontSize;
      }
      
      const finalBottomY = textStartY + (bestMetrics.lines.length - 1) * bestMetrics.lineHeight;
      if (finalBottomY > maxY) {
        textStartY = maxY - bestMetrics.totalTextHeight + bestMetrics.fontSize;
      }
      
      // Render text lines
      bestMetrics.lines.forEach((line, index) => {
        const y = textStartY + (index * bestMetrics.lineHeight);
        if (y <= maxY) {
          ctx.fillText(line, 300, y);
        }
      });
    }

    const background = new Image();
    background.crossOrigin = "anonymous";
    
    background.onload = async () => {
      ctx.drawImage(background, 0, 0, 600, 600);
      
      const processImage = async () => {
        const dataUrl = canvas.toDataURL('image/png');
        
        setDisclaimerModal({
          open: true,
          imageUrl: dataUrl,
          bitlyLink: null,
          title: `${cleanedName} - ${type.charAt(0).toUpperCase() + type.slice(1)} Disclaimer`,
          type,
          disclaimerText,
          pendingGeneration: true
        });
      };
      
      if (client?.blackLogo) {
        const logo = new Image();
        logo.crossOrigin = 'anonymous';
        logo.onload = async () => {
          // Use safe zone fitting logic
          const logoPlacement = fitLogoToSafeZone(logo.width, logo.height);
          
          // Draw logo with calculated placement
          ctx.drawImage(logo, logoPlacement.x, logoPlacement.y, logoPlacement.width, logoPlacement.height);
          
          // Add dealer info
          ctx.fillStyle = 'black';
          ctx.font = 'bold 30px "Capellina Caps", serif';
          ctx.textAlign = 'center';
          ctx.fillText(cleanedName, 300, 540);
          
          const fullAddress = `${street}${street ? ', ' : ''}${cityStateZip}`.trim();
          if (fullAddress) {
            ctx.font = 'bold 20px "Capellina Caps", serif';
            ctx.fillText(fullAddress, 300, 558);
          }
          
          // Add disclaimer text with appropriate spacing after logo
          // Use bottom of logo + margin for text start
          // Set minimum Y position to ensure text never overlaps with "Event Disclaimer" header
          const TEXT_SAFE_ZONE_MIN_Y = 175; // Minimum Y position for text to start (increased for better text sizing)
          const textStartY = Math.max(logoPlacement.bottomY + 50, TEXT_SAFE_ZONE_MIN_Y);
          addDisclaimerText(ctx, disclaimerText, textStartY);
          
          await processImage();
        };
        logo.onerror = async () => {
          addDealerInfoWithoutLogo(ctx, cleanedName, street, cityStateZip);
          addDisclaimerText(ctx, disclaimerText, 200);
          await processImage();
        };
        logo.src = client.blackLogo;
      } else {
        addDealerInfoWithoutLogo(ctx, cleanedName, street, cityStateZip);
        addDisclaimerText(ctx, disclaimerText, 200);
        await processImage();
      }
    };
    
    background.onerror = () => {
      alert('Error loading disclaimer template');
    };
    
    background.src = `${window.location.origin}/disclaimer-template.jpg`;
    
  } catch (error) {
    console.error('Error generating disclaimer:', error);
    alert(`Error generating ${type} disclaimer: ${error.message}`);
  } finally {
    setGeneratingDisclaimer(false);
  }
};

const generateDisclaimerLink = async () => {
  if (!disclaimerModal.imageUrl || !disclaimerModal.type) return;
  
  setGeneratingDisclaimer(true);
  
  try {
    // Upload to Firebase Storage
    console.log('Uploading image to Firebase Storage...');
    const imageUrl = await uploadDisclaimerToFirebase(disclaimerModal.imageUrl, disclaimerModal.type);
    
    // Create Bitly link
    console.log('Creating Bitly link...');
    const fullBitlyLink = await createBitlyLink(imageUrl);
    
    // Clean up the link - remove https:// or http://
    const cleanBitlyLink = fullBitlyLink.replace(/^https?:\/\//, '');
    
    // Save the cleaned Bitly link to the client profile
    const fieldName = disclaimerModal.type === 'generic' ? 'genericDisclaimerLink' : 'specialDisclaimerLink';
    const updateData = { [fieldName]: cleanBitlyLink };
    
    // Update local state
    setClient(prev => ({ ...prev, ...updateData }));
    
    // Update Firebase
    await updateDoc(doc(db, 'clients', id), updateData);
    
    // Update modal with the link
    setDisclaimerModal(prev => ({
      ...prev,
      bitlyLink: cleanBitlyLink,
      pendingGeneration: false
    }));
    
    console.log('Disclaimer link saved:', cleanBitlyLink);
    
  } catch (error) {
    console.error('Error creating link:', error);
    alert(`Failed to create link: ${error.message}`);
  } finally {
    setGeneratingDisclaimer(false);
  }
};


const handleDisclaimerChange = (field, value) => {
  setClient(prev => ({ ...prev, [field]: value }));
};

// Helper: force-download any URL (works for cross-origin Firebase Storage URLs)
const handleForceDownload = async (url, filename) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename || 'download';
    link.click();
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error('Download failed:', err);
    // Fallback: open in new tab
    window.open(url, '_blank');
  }
};

// Function to download disclaimer
const downloadDisclaimer = () => {
  const link = document.createElement('a');
  link.download = `${client.name} Disclaimer.png`;
  link.href = disclaimerModal.imageUrl;
  link.click();
};

const generateFacebookAd = async () => {
  const adName = [selectedAdType, selectedMonth, selectedYear].filter(Boolean).join(' - ');

  if (!client || !selectedYear || !selectedMonth || !selectedAdType) {
    alert('Please select year, month, and choose an ad type');
    return;
  }
  if (!client.manufacturer) {
    alert('Client manufacturer must be set to generate Facebook ads');
    return;
  }

  setGeneratingFacebookAd(true);

  try {
    const cleanedName = (client?.name || '')
      .replace(/\n|\r/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const canvas = document.createElement('canvas');
    canvas.width = 2500;
    canvas.height = 2500;
    const ctx = canvas.getContext('2d');

    // Get template from Firebase Storage
    const templateRef = ref(storage, `facebook-ad-templates/${selectedYear}/${selectedMonth}/${selectedAdType}/${client.manufacturer}.jpg`);
    
    try {
      const templatePath = await getDownloadURL(templateRef);
      console.log('[template] Loading from Firebase Storage:', templatePath);

      const background = new Image();
      background.crossOrigin = 'anonymous';

      background.onload = async () => {
        ctx.drawImage(background, 0, 0, 2500, 2500);

        const processImage = async () => {
          const dataUrl = canvas.toDataURL('image/png');
          setFacebookAdsModal({
            open: true,
            imageUrl: dataUrl,
            title: `${cleanedName} - ${adName}`,
            adName,
            year: selectedYear,
            month: selectedMonth,
            adType: selectedAdType,
          });
        };

        // Load placement configuration - handle both old and new formats
        let logoPlacement = null;
        let addressConfig = null;
        let phoneConfig = null;
        
        try {
          // Try to load placement.json
          const placementRef = ref(storage, `facebook-ad-templates/${selectedYear}/${selectedMonth}/${selectedAdType}/placement.json`);
          const placementUrl = await getDownloadURL(placementRef);
          const response = await fetch(placementUrl, { cache: 'no-store' });
          
          if (response.ok) {
            const raw = await response.json();
            console.log('[placement] Raw placement data:', raw);
            
            // Check if this is new format (has logo/address properties) or old format
            if (raw.logo || raw.address || raw.dealerName) {
              // New format
              console.log('[placement] Using NEW format');
              logoPlacement = raw.logo;
              addressConfig = raw.address;
              phoneConfig = raw.phone;
            } else if (raw.box || raw.x !== undefined) {
              // Old format - convert to logo placement
              console.log('[placement] Using OLD format, converting...');
              logoPlacement = {
                box: raw.box || {
                  x: Number(raw.x ?? 0),
                  y: Number(raw.y ?? 0),
                  width: Number(raw.maxWidth ?? 300),
                  height: Number(raw.maxHeight ?? 120)
                },
                rotation: Number(raw.rotation ?? 0),
                objectFit: raw.objectFit || 'contain',
                padding: Number(raw.padding ?? 0),
                horizontalAlign: raw.horizontalAlign || 'center',
                verticalAlign: raw.verticalAlign || 'center'
              };
            }
          }
        } catch (placementError) {
          console.warn('[placement] No placement.json found, using defaults');
          // Use default logo placement
          logoPlacement = {
            box: { x: 500, y: 300, width: 1000, height: 500 },
            rotation: 0,
            objectFit: 'contain',
            padding: 0,
            horizontalAlign: 'center',
            verticalAlign: 'center'
          };
        }

        // Draw logo if available
        if (client?.blackLogo && logoPlacement) {
          const logo = new Image();
          logo.crossOrigin = 'anonymous';
          
          logo.onload = async () => {
            console.log('[logo] Drawing logo with placement:', logoPlacement);
            
            // Use the existing positionLogoInBox function
            const position = positionLogoInBox(logo, logoPlacement);

            if (logoPlacement.rotation) {
              ctx.save();
              ctx.translate(position.x + position.width / 2, position.y + position.height / 2);
              ctx.rotate((logoPlacement.rotation * Math.PI) / 180);
              ctx.drawImage(logo, -position.width / 2, -position.height / 2, position.width, position.height);
              ctx.restore();
            } else {
              ctx.drawImage(logo, position.x, position.y, position.width, position.height);
            }

            // Draw address if configured
            if (addressConfig && addressConfig.enabled) {
              const street = client.street1?.trim() || '';
              const city = client.city?.trim() || '';
              const state = client.state?.trim() || '';
              const zip = client.zip?.trim() || '';
              
              let fullAddress = '';
              
              if (addressConfig.twoLines) {
                // Two-line format: street on line 1, city/state/zip on line 2
                let line2 = '';
                if (city) line2 += city;
                if (state) {
                  if (line2) line2 += ', ';
                  line2 += state;
                }
                if (zip) {
                  if (line2) line2 += ' ';
                  line2 += zip;
                }
                
                if (street) fullAddress += street + ',';  // Add comma after street
                if (line2) {
                  if (fullAddress) fullAddress += '\n';
                  fullAddress += line2;
                }
              } else {
                // Single-line format: all on one line with commas
                if (street) fullAddress += street;
                if (city) {
                  if (fullAddress) fullAddress += ', ';
                  fullAddress += city;
                }
                if (state) {
                  if (city) fullAddress += ', ';
                  else if (fullAddress) fullAddress += ', ';
                  fullAddress += state;
                }
                if (zip) {
                  if (fullAddress) fullAddress += ' ';
                  fullAddress += zip;
                }
              }
              
              if (fullAddress) {
                console.log('[address] Drawing address:', fullAddress);
                drawTextElement(ctx, fullAddress, addressConfig);
              }
            }

            // Draw phone if configured
            if (phoneConfig && phoneConfig.enabled && client.phone) {
              console.log('[phone] Drawing phone:', client.phone);
              drawTextElement(ctx, client.phone, phoneConfig);
            }

            await processImage();
          };
          
          logo.onerror = async () => {
            console.warn('Logo failed to load, generating ad without logo');
            
            // Still draw address if configured, even without logo
            if (addressConfig && addressConfig.enabled) {
              const street = client.street1?.trim() || '';
              const city = client.city?.trim() || '';
              const state = client.state?.trim() || '';
              const zip = client.zip?.trim() || '';
              
              let fullAddress = '';
              
              if (addressConfig.twoLines) {
                // Two-line format: street on line 1, city/state/zip on line 2
                let line2 = '';
                if (city) line2 += city;
                if (state) {
                  if (line2) line2 += ', ';
                  line2 += state;
                }
                if (zip) {
                  if (line2) line2 += ' ';
                  line2 += zip;
                }
                
                if (street) fullAddress += street + ',';  // Add comma after street
                if (line2) {
                  if (fullAddress) fullAddress += '\n';
                  fullAddress += line2;
                }
              } else {
                // Single-line format: all on one line with commas
                if (street) fullAddress += street;
                if (city) {
                  if (fullAddress) fullAddress += ', ';
                  fullAddress += city;
                }
                if (state) {
                  if (city) fullAddress += ', ';
                  else if (fullAddress) fullAddress += ', ';
                  fullAddress += state;
                }
                if (zip) {
                  if (fullAddress) fullAddress += ' ';
                  fullAddress += zip;
                }
              }
              
              if (fullAddress) {
                drawTextElement(ctx, fullAddress, addressConfig);
              }
            }
            
            // Draw phone if configured
            if (phoneConfig && phoneConfig.enabled && client.phone) {
              console.log('[phone] Drawing phone:', client.phone);
              drawTextElement(ctx, client.phone, phoneConfig);
            }
            
            await processImage();
          };
          
          logo.src = client.blackLogo;
        } else {
          // No logo, but still draw address if configured
          if (addressConfig && addressConfig.enabled) {
            const street = client.street1?.trim() || '';
            const city = client.city?.trim() || '';
            const state = client.state?.trim() || '';
            const zip = client.zip?.trim() || '';
            
            let fullAddress = '';
            
            if (addressConfig.twoLines) {
              // Two-line format: street on line 1, city/state/zip on line 2
              let line2 = '';
              if (city) line2 += city;
              if (state) {
                if (line2) line2 += ', ';
                line2 += state;
              }
              if (zip) {
                if (line2) line2 += ' ';
                line2 += zip;
              }
              
              if (street) fullAddress += street + ',';  // Add comma after street
              if (line2) {
                if (fullAddress) fullAddress += '\n';
                fullAddress += line2;
              }
            } else {
              // Single-line format: all on one line with commas
              if (street) fullAddress += street;
              if (city) {
                if (fullAddress) fullAddress += ', ';
                fullAddress += city;
              }
              if (state) {
                if (city) fullAddress += ', ';
                else if (fullAddress) fullAddress += ', ';
                fullAddress += state;
              }
              if (zip) {
                if (fullAddress) fullAddress += ' ';
                fullAddress += zip;
              }
            }
            
            if (fullAddress) {
              drawTextElement(ctx, fullAddress, addressConfig);
            }
          }
          
          // Draw phone if configured
          if (phoneConfig && phoneConfig.enabled && client.phone) {
            console.log('[phone] Drawing phone:', client.phone);
            drawTextElement(ctx, client.phone, phoneConfig);
          }
          
          await processImage();
        }
      };

      background.onerror = () => {
        console.error('Background template failed to load from Firebase Storage');
        alert(`Error loading Facebook ad template from Firebase Storage`);
      };

      background.src = templatePath;

    } catch (storageError) {
      if (storageError.code === 'storage/object-not-found') {
        alert(`Template not found: ${client.manufacturer} template for ${selectedMonth} ${selectedYear} - ${selectedAdType}`);
      } else {
        console.error('Firebase Storage error:', storageError);
        alert(`Error accessing Facebook ad template: ${storageError.message}`);
      }
    }

  } catch (error) {
    console.error('Error generating Facebook ad:', error);
    alert(`Error generating Facebook ad: ${error.message}`);
  } finally {
    setGeneratingFacebookAd(false);
  }
};


// Also move this INSIDE the component
const downloadFacebookAd = () => {
  const link = document.createElement('a');
  link.download = `${client.name}_${facebookAdsModal.adName}.jpg`;
  link.href = facebookAdsModal.imageUrl;
  link.click();
  
  setSelectedYear('');
  setSelectedMonth('');
  setSelectedAdType('');
  
  setFacebookAdsModal({ open: false, imageUrl: '', title: '', adName: '', year: '', month: '', adType: '' });
};

// Profile Picture generation function
const generateProfilePicture = async () => {
  const profileName = [selectedProfileType, selectedProfileMonth, selectedProfileYear].filter(Boolean).join(' - ');

  if (!client || !selectedProfileYear || !selectedProfileMonth || !selectedProfileType) {
    alert('Please select year, month, and choose a profile picture type');
    return;
  }
  if (!client.manufacturer) {
    alert('Client manufacturer must be set to generate profile pictures');
    return;
  }

  setGeneratingProfilePicture(true);

  try {
    const cleanedName = (client?.name || '')
      .replace(/\n|\r/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const canvas = document.createElement('canvas');
    canvas.width = 2500;
    canvas.height = 2500;
    const ctx = canvas.getContext('2d');

    // Get template from Firebase Storage - use facebook-profile-pictures folder
    const templateRef = ref(storage, `facebook-profile-pictures/${selectedProfileYear}/${selectedProfileMonth}/${selectedProfileType}/${client.manufacturer}.jpg`);
    
    try {
      const templatePath = await getDownloadURL(templateRef);
      console.log('[profile-template] Loading from Firebase Storage:', templatePath);

      const background = new Image();
      background.crossOrigin = 'anonymous';

      background.onload = async () => {
        ctx.drawImage(background, 0, 0, 2500, 2500);

        const processImage = async () => {
          const dataUrl = canvas.toDataURL('image/png');
          setProfilePictureModal({
            open: true,
            imageUrl: dataUrl,
            title: `${cleanedName} - ${profileName}`,
            adName: profileName,
            year: selectedProfileYear,
            month: selectedProfileMonth,
            adType: selectedProfileType,
          });
        };

        // Load placement configuration
        let logoPlacement = null;
        let addressConfig = null;
        let phoneConfig = null;
        
        try {
          const placementRef = ref(storage, `facebook-profile-pictures/${selectedProfileYear}/${selectedProfileMonth}/${selectedProfileType}/placement.json`);
          const placementUrl = await getDownloadURL(placementRef);
          const response = await fetch(placementUrl, { cache: 'no-store' });
          
          if (response.ok) {
            const raw = await response.json();
            console.log('[profile-placement] Raw placement data:', raw);
            
            if (raw.logo || raw.address || raw.dealerName) {
              console.log('[profile-placement] Using NEW format');
              logoPlacement = raw.logo;
              addressConfig = raw.address;
              phoneConfig = raw.phone;
            } else if (raw.box || raw.x !== undefined) {
              console.log('[profile-placement] Using OLD format, converting...');
              logoPlacement = {
                box: raw.box || {
                  x: Number(raw.x ?? 0),
                  y: Number(raw.y ?? 0),
                  width: Number(raw.maxWidth ?? 300),
                  height: Number(raw.maxHeight ?? 120)
                },
                rotation: Number(raw.rotation ?? 0),
                objectFit: raw.objectFit || 'contain',
                padding: Number(raw.padding ?? 0),
                horizontalAlign: raw.horizontalAlign || 'center',
                verticalAlign: raw.verticalAlign || 'center'
              };
            }
          }
        } catch (placementError) {
          console.warn('[profile-placement] No placement.json found, using defaults');
          logoPlacement = {
            box: { x: 500, y: 300, width: 1000, height: 500 },
            rotation: 0,
            objectFit: 'contain',
            padding: 0,
            horizontalAlign: 'center',
            verticalAlign: 'center'
          };
        }

        // Draw logo if available
        if (client?.blackLogo && logoPlacement) {
          const logo = new Image();
          logo.crossOrigin = 'anonymous';
          
          logo.onload = async () => {
            console.log('[profile-logo] Drawing logo with placement:', logoPlacement);
            
            const position = positionLogoInBox(logo, logoPlacement);

            if (logoPlacement.rotation) {
              ctx.save();
              ctx.translate(position.x + position.width / 2, position.y + position.height / 2);
              ctx.rotate((logoPlacement.rotation * Math.PI) / 180);
              ctx.drawImage(logo, -position.width / 2, -position.height / 2, position.width, position.height);
              ctx.restore();
            } else {
              ctx.drawImage(logo, position.x, position.y, position.width, position.height);
            }

            // Draw address if configured
            if (addressConfig && addressConfig.enabled) {
              const street = client.street1?.trim() || '';
              const city = client.city?.trim() || '';
              const state = client.state?.trim() || '';
              const zip = client.zip?.trim() || '';
              
              let fullAddress = '';
              
              if (addressConfig.twoLines) {
                let line2 = '';
                if (city) line2 += city;
                if (state) {
                  if (line2) line2 += ', ';
                  line2 += state;
                }
                if (zip) {
                  if (line2) line2 += ' ';
                  line2 += zip;
                }
                
                if (street) fullAddress += street + ',';
                if (line2) {
                  if (fullAddress) fullAddress += '\n';
                  fullAddress += line2;
                }
              } else {
                if (street) fullAddress += street;
                if (city) {
                  if (fullAddress) fullAddress += ', ';
                  fullAddress += city;
                }
                if (state) {
                  if (city) fullAddress += ', ';
                  else if (fullAddress) fullAddress += ', ';
                  fullAddress += state;
                }
                if (zip) {
                  if (fullAddress) fullAddress += ' ';
                  fullAddress += zip;
                }
              }
              
              if (fullAddress) {
                console.log('[profile-address] Drawing address:', fullAddress);
                drawTextElement(ctx, fullAddress, addressConfig);
              }
            }

            // Draw phone if configured
            if (phoneConfig && phoneConfig.enabled && client.phone) {
              console.log('[profile-phone] Drawing phone:', client.phone);
              drawTextElement(ctx, client.phone, phoneConfig);
            }

            await processImage();
          };
          
          logo.onerror = async () => {
            console.warn('Profile picture logo failed to load, generating without logo');
            
            // Still draw address if configured
            if (addressConfig && addressConfig.enabled) {
              const street = client.street1?.trim() || '';
              const city = client.city?.trim() || '';
              const state = client.state?.trim() || '';
              const zip = client.zip?.trim() || '';
              
              let fullAddress = '';
              
              if (addressConfig.twoLines) {
                let line2 = '';
                if (city) line2 += city;
                if (state) {
                  if (line2) line2 += ', ';
                  line2 += state;
                }
                if (zip) {
                  if (line2) line2 += ' ';
                  line2 += zip;
                }
                
                if (street) fullAddress += street + ',';
                if (line2) {
                  if (fullAddress) fullAddress += '\n';
                  fullAddress += line2;
                }
              } else {
                if (street) fullAddress += street;
                if (city) {
                  if (fullAddress) fullAddress += ', ';
                  fullAddress += city;
                }
                if (state) {
                  if (city) fullAddress += ', ';
                  else if (fullAddress) fullAddress += ', ';
                  fullAddress += state;
                }
                if (zip) {
                  if (fullAddress) fullAddress += ' ';
                  fullAddress += zip;
                }
              }
              
              if (fullAddress) {
                drawTextElement(ctx, fullAddress, addressConfig);
              }
            }
            
            // Draw phone if configured
            if (phoneConfig && phoneConfig.enabled && client.phone) {
              drawTextElement(ctx, client.phone, phoneConfig);
            }
            
            await processImage();
          };

          logo.src = client.blackLogo;
        } else {
          await processImage();
        }
      };

      background.onerror = () => {
        console.error('Profile picture template failed to load from Firebase Storage');
        alert(`Error loading profile picture template from Firebase Storage`);
      };

      background.src = templatePath;

    } catch (storageError) {
      if (storageError.code === 'storage/object-not-found') {
        alert(`Template not found: ${client.manufacturer} template for ${selectedProfileMonth} ${selectedProfileYear} - ${selectedProfileType}`);
      } else {
        console.error('Firebase Storage error:', storageError);
        alert(`Error accessing profile picture template: ${storageError.message}`);
      }
    }

  } catch (error) {
    console.error('Error generating profile picture:', error);
    alert(`Error generating profile picture: ${error.message}`);
  } finally {
    setGeneratingProfilePicture(false);
  }
};

const downloadProfilePicture = () => {
  const link = document.createElement('a');
  link.download = `${client.name}_${profilePictureModal.adName}.jpg`;
  link.href = profilePictureModal.imageUrl;
  link.click();
  
  setSelectedProfileYear('');
  setSelectedProfileMonth('');
  setSelectedProfileType('');
  
  setProfilePictureModal({ open: false, imageUrl: '', title: '', adName: '', year: '', month: '', adType: '' });
};

// Header generation function
const generateHeader = async () => {
  const headerName = [selectedHeaderType, selectedHeaderMonth, selectedHeaderYear].filter(Boolean).join(' - ');

  if (!client || !selectedHeaderYear || !selectedHeaderMonth || !selectedHeaderType) {
    alert('Please select year, month, and choose a header type');
    return;
  }
  if (!client.manufacturer) {
    alert('Client manufacturer must be set to generate headers');
    return;
  }

  setGeneratingHeader(true);

  try {
    const cleanedName = (client?.name || '')
      .replace(/\n|\r/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const canvas = document.createElement('canvas');
    canvas.width = 820;
    canvas.height = 360;
    const ctx = canvas.getContext('2d');

    // Get template from Firebase Storage - use facebook-headers folder
    const templateRef = ref(storage, `facebook-headers/${selectedHeaderYear}/${selectedHeaderMonth}/${selectedHeaderType}/${client.manufacturer}.jpg`);
    
    try {
      const templatePath = await getDownloadURL(templateRef);
      console.log('[header-template] Loading from Firebase Storage:', templatePath);

      const background = new Image();
      background.crossOrigin = 'anonymous';

      background.onload = async () => {
        ctx.drawImage(background, 0, 0, 820, 360);

        const processImage = async () => {
          const dataUrl = canvas.toDataURL('image/png');
          setHeaderModal({
            open: true,
            imageUrl: dataUrl,
            title: `${cleanedName} - ${headerName}`,
            adName: headerName,
            year: selectedHeaderYear,
            month: selectedHeaderMonth,
            adType: selectedHeaderType,
          });
        };

        // Load placement configuration
        let logoPlacement = null;
        let addressConfig = null;
        let phoneConfig = null;
        
        try {
          const placementRef = ref(storage, `facebook-headers/${selectedHeaderYear}/${selectedHeaderMonth}/${selectedHeaderType}/placement.json`);
          const placementUrl = await getDownloadURL(placementRef);
          const response = await fetch(placementUrl, { cache: 'no-store' });
          
          if (response.ok) {
            const raw = await response.json();
            console.log('[header-placement] Raw placement data:', raw);
            
            if (raw.logo || raw.address || raw.dealerName) {
              console.log('[header-placement] Using NEW format');
              logoPlacement = raw.logo;
              addressConfig = raw.address;
              phoneConfig = raw.phone;
            } else if (raw.box || raw.x !== undefined) {
              console.log('[header-placement] Using OLD format, converting...');
              logoPlacement = {
                box: raw.box || {
                  x: Number(raw.x ?? 0),
                  y: Number(raw.y ?? 0),
                  width: Number(raw.maxWidth ?? 300),
                  height: Number(raw.maxHeight ?? 120)
                },
                rotation: Number(raw.rotation ?? 0),
                objectFit: raw.objectFit || 'contain',
                padding: Number(raw.padding ?? 0),
                horizontalAlign: raw.horizontalAlign || 'center',
                verticalAlign: raw.verticalAlign || 'center'
              };
            }
          }
        } catch (placementError) {
          console.warn('[header-placement] No placement.json found, using defaults');
          logoPlacement = {
            box: { x: 164, y: 72, width: 328, height: 164 },
            rotation: 0,
            objectFit: 'contain',
            padding: 0,
            horizontalAlign: 'center',
            verticalAlign: 'center'
          };
        }

        // Draw logo if available
        if (client?.blackLogo && logoPlacement) {
          const logo = new Image();
          logo.crossOrigin = 'anonymous';
          
          logo.onload = async () => {
            console.log('[header-logo] Drawing logo with placement:', logoPlacement);
            
            const position = positionLogoInBox(logo, logoPlacement);

            if (logoPlacement.rotation) {
              ctx.save();
              ctx.translate(position.x + position.width / 2, position.y + position.height / 2);
              ctx.rotate((logoPlacement.rotation * Math.PI) / 180);
              ctx.drawImage(logo, -position.width / 2, -position.height / 2, position.width, position.height);
              ctx.restore();
            } else {
              ctx.drawImage(logo, position.x, position.y, position.width, position.height);
            }

            // Draw address if configured
            if (addressConfig && addressConfig.enabled) {
              const street = client.street1?.trim() || '';
              const city = client.city?.trim() || '';
              const state = client.state?.trim() || '';
              const zip = client.zip?.trim() || '';
              
              let fullAddress = '';
              
              if (addressConfig.twoLines) {
                let line2 = '';
                if (city) line2 += city;
                if (state) {
                  if (line2) line2 += ', ';
                  line2 += state;
                }
                if (zip) {
                  if (line2) line2 += ' ';
                  line2 += zip;
                }
                
                if (street) fullAddress += street + ',';
                if (line2) {
                  if (fullAddress) fullAddress += '\n';
                  fullAddress += line2;
                }
              } else {
                if (street) fullAddress += street;
                if (city) {
                  if (fullAddress) fullAddress += ', ';
                  fullAddress += city;
                }
                if (state) {
                  if (city) fullAddress += ', ';
                  else if (fullAddress) fullAddress += ', ';
                  fullAddress += state;
                }
                if (zip) {
                  if (fullAddress) fullAddress += ' ';
                  fullAddress += zip;
                }
              }
              
              if (fullAddress) {
                console.log('[header-address] Drawing address:', fullAddress);
                drawTextElement(ctx, fullAddress, addressConfig);
              }
            }

            // Draw phone if configured
            if (phoneConfig && phoneConfig.enabled && client.phone) {
              console.log('[header-phone] Drawing phone:', client.phone);
              drawTextElement(ctx, client.phone, phoneConfig);
            }

            await processImage();
          };
          
          logo.onerror = async () => {
            console.warn('Header logo failed to load, generating without logo');
            
            // Still draw address if configured
            if (addressConfig && addressConfig.enabled) {
              const street = client.street1?.trim() || '';
              const city = client.city?.trim() || '';
              const state = client.state?.trim() || '';
              const zip = client.zip?.trim() || '';
              
              let fullAddress = '';
              
              if (addressConfig.twoLines) {
                let line2 = '';
                if (city) line2 += city;
                if (state) {
                  if (line2) line2 += ', ';
                  line2 += state;
                }
                if (zip) {
                  if (line2) line2 += ' ';
                  line2 += zip;
                }
                
                if (street) fullAddress += street + ',';
                if (line2) {
                  if (fullAddress) fullAddress += '\n';
                  fullAddress += line2;
                }
              } else {
                if (street) fullAddress += street;
                if (city) {
                  if (fullAddress) fullAddress += ', ';
                  fullAddress += city;
                }
                if (state) {
                  if (city) fullAddress += ', ';
                  else if (fullAddress) fullAddress += ', ';
                  fullAddress += state;
                }
                if (zip) {
                  if (fullAddress) fullAddress += ' ';
                  fullAddress += zip;
                }
              }
              
              if (fullAddress) {
                drawTextElement(ctx, fullAddress, addressConfig);
              }
            }
            
            // Draw phone if configured
            if (phoneConfig && phoneConfig.enabled && client.phone) {
              drawTextElement(ctx, client.phone, phoneConfig);
            }
            
            await processImage();
          };

          logo.src = client.blackLogo;
        } else {
          await processImage();
        }
      };

      background.onerror = () => {
        console.error('Header template failed to load from Firebase Storage');
        alert(`Error loading header template from Firebase Storage`);
      };

      background.src = templatePath;

    } catch (storageError) {
      if (storageError.code === 'storage/object-not-found') {
        alert(`Template not found: ${client.manufacturer} template for ${selectedHeaderMonth} ${selectedHeaderYear} - ${selectedHeaderType}`);
      } else {
        console.error('Firebase Storage error:', storageError);
        alert(`Error accessing header template: ${storageError.message}`);
      }
    }

  } catch (error) {
    console.error('Error generating header:', error);
    alert(`Error generating header: ${error.message}`);
  } finally {
    setGeneratingHeader(false);
  }
};

const downloadHeader = async () => {
  // High-resolution export: 3417 x 1500 (4.167x scale from 820x360)
  const SCALE_FACTOR = 3417 / 820;
  const HIGH_RES_WIDTH = 3417;
  const HIGH_RES_HEIGHT = 1500;
  
  try {
    const canvas = document.createElement('canvas');
    canvas.width = HIGH_RES_WIDTH;
    canvas.height = HIGH_RES_HEIGHT;
    const ctx = canvas.getContext('2d');

    // Get template from Firebase Storage
    const templateRef = ref(storage, `facebook-headers/${headerModal.year}/${headerModal.month}/${headerModal.adType}/${client.manufacturer}.jpg`);
    const templatePath = await getDownloadURL(templateRef);

    const background = new Image();
    background.crossOrigin = 'anonymous';

    await new Promise((resolve, reject) => {
      background.onload = async () => {
        // Draw background at high resolution
        ctx.drawImage(background, 0, 0, HIGH_RES_WIDTH, HIGH_RES_HEIGHT);

        // Load placement configuration
        let logoPlacement = null;
        let addressConfig = null;
        let phoneConfig = null;
        
        try {
          const placementRef = ref(storage, `facebook-headers/${headerModal.year}/${headerModal.month}/${headerModal.adType}/placement.json`);
          const placementUrl = await getDownloadURL(placementRef);
          const response = await fetch(placementUrl, { cache: 'no-store' });
          
          if (response.ok) {
            const raw = await response.json();
            
            // Scale the placement configuration
            const scaleConfig = (config) => {
              if (!config) return null;
              return {
                ...config,
                box: config.box ? {
                  x: config.box.x * SCALE_FACTOR,
                  y: config.box.y * SCALE_FACTOR,
                  width: config.box.width * SCALE_FACTOR,
                  height: config.box.height * SCALE_FACTOR
                } : config.box,
                fontSize: config.fontSize ? config.fontSize * SCALE_FACTOR : config.fontSize,
                minFontSize: config.minFontSize ? config.minFontSize * SCALE_FACTOR : config.minFontSize,
                maxFontSize: config.maxFontSize ? config.maxFontSize * SCALE_FACTOR : config.maxFontSize,
                padding: config.padding ? config.padding * SCALE_FACTOR : config.padding,
                borderRadius: config.borderRadius ? config.borderRadius * SCALE_FACTOR : config.borderRadius,
                textShadow: config.textShadow ? config.textShadow.replace(/(\d+)px/g, (match, p1) => `${Math.round(parseInt(p1) * SCALE_FACTOR)}px`) : config.textShadow
              };
            };
            
            if (raw.logo || raw.address || raw.dealerName) {
              logoPlacement = scaleConfig(raw.logo);
              addressConfig = scaleConfig(raw.address);
              phoneConfig = scaleConfig(raw.phone);
            } else if (raw.box || raw.x !== undefined) {
              logoPlacement = scaleConfig({
                box: raw.box || {
                  x: Number(raw.x ?? 0),
                  y: Number(raw.y ?? 0),
                  width: Number(raw.maxWidth ?? 300),
                  height: Number(raw.maxHeight ?? 120)
                },
                rotation: Number(raw.rotation ?? 0),
                objectFit: raw.objectFit || 'contain',
                padding: Number(raw.padding ?? 0),
                horizontalAlign: raw.horizontalAlign || 'center',
                verticalAlign: raw.verticalAlign || 'center'
              });
            }
          }
        } catch (placementError) {
          console.warn('[header-download] No placement.json found, using scaled defaults');
          logoPlacement = {
            box: { 
              x: 164 * SCALE_FACTOR, 
              y: 72 * SCALE_FACTOR, 
              width: 328 * SCALE_FACTOR, 
              height: 164 * SCALE_FACTOR 
            },
            rotation: 0,
            objectFit: 'contain',
            padding: 0,
            horizontalAlign: 'center',
            verticalAlign: 'center'
          };
        }

        // Draw logo if available
        if (client?.blackLogo && logoPlacement) {
          const logo = new Image();
          logo.crossOrigin = 'anonymous';
          
          await new Promise((logoResolve) => {
            logo.onload = () => {
              const position = positionLogoInBox(logo, logoPlacement);

              if (logoPlacement.rotation) {
                ctx.save();
                ctx.translate(position.x + position.width / 2, position.y + position.height / 2);
                ctx.rotate((logoPlacement.rotation * Math.PI) / 180);
                ctx.drawImage(logo, -position.width / 2, -position.height / 2, position.width, position.height);
                ctx.restore();
              } else {
                ctx.drawImage(logo, position.x, position.y, position.width, position.height);
              }

              logoResolve();
            };
            
            logo.onerror = () => {
              console.warn('Logo failed to load for high-res download');
              logoResolve();
            };

            logo.src = client.blackLogo;
          });
        }

        // Draw address if configured
        if (addressConfig && addressConfig.enabled) {
          const street = client.street1?.trim() || '';
          const city = client.city?.trim() || '';
          const state = client.state?.trim() || '';
          const zip = client.zip?.trim() || '';
          
          let fullAddress = '';
          
          if (addressConfig.twoLines) {
            let line2 = '';
            if (city) line2 += city;
            if (state) {
              if (line2) line2 += ', ';
              line2 += state;
            }
            if (zip) {
              if (line2) line2 += ' ';
              line2 += zip;
            }
            
            if (street) fullAddress += street + ',';
            if (line2) {
              if (fullAddress) fullAddress += '\n';
              fullAddress += line2;
            }
          } else {
            if (street) fullAddress += street;
            if (city) {
              if (fullAddress) fullAddress += ', ';
              fullAddress += city;
            }
            if (state) {
              if (city) fullAddress += ', ';
              else if (fullAddress) fullAddress += ', ';
              fullAddress += state;
            }
            if (zip) {
              if (fullAddress) fullAddress += ' ';
              fullAddress += zip;
            }
          }
          
          if (fullAddress) {
            drawTextElement(ctx, fullAddress, addressConfig);
          }
        }

        // Draw phone if configured
        if (phoneConfig && phoneConfig.enabled && client.phone) {
          drawTextElement(ctx, client.phone, phoneConfig);
        }

        resolve();
      };

      background.onerror = () => {
        reject(new Error('Failed to load header template for high-res download'));
      };

      background.src = templatePath;
    });

    // Convert to blob and download
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `${client.name}_${headerModal.adName}.jpg`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      
      setSelectedHeaderYear('');
      setSelectedHeaderMonth('');
      setSelectedHeaderType('');
      
      setHeaderModal({ open: false, imageUrl: '', title: '', adName: '', year: '', month: '', adType: '' });
    }, 'image/jpeg', 0.95);

  } catch (error) {
    console.error('Error downloading high-res header:', error);
    alert(`Error downloading header: ${error.message}`);
  }
};


const generateVerbiage = async (templateName) => {
  if (!client) return;
  
  setGeneratingVerbiage(true);
  
  try {
    let templateContent;
    const isCustomTemplate = client.customTemplates && client.customTemplates[templateName];
    
    // Check if this is a custom template first
    if (isCustomTemplate) {
      templateContent = client.customTemplates[templateName];
    } else {
      // Fall back to default templates
      const templateUrl = TEMPLATES[templateName];
      
      if (!templateUrl) {
        throw new Error(`Template not found: ${templateName}`);
      }
      
      const response = await fetch(templateUrl);
      if (!response.ok) {
        throw new Error(`Failed to load template: ${templateName}`);
      }
      templateContent = await response.text();
    }
    
    // Prepare replacement data from client profile
    const dealershipName = client.name?.trim() || '';
    const street = client.street1?.trim() || '';
    const cityStateZip = `${client.city?.trim() || ''}, ${client.state?.trim() || ''} ${client.zip?.trim() || ''}`.trim();
    const disclaimer = client.disclaimerLink?.trim() || 'No disclaimer provided';
    
    // Convert to bold unicode (matching your Python script)
    const boldName = toBoldUnicode(dealershipName.toUpperCase());
    const boldStreet = toBoldUnicode(street.toUpperCase());
    const boldCityStateZip = toBoldUnicode(cityStateZip.toUpperCase());
    
    // Perform replacements (matching your Python script)
    const filledContent = templateContent
      .replace(/<<DEALERSHIP_NAME>>/g, boldName)
      .replace(/<<Address>>/g, boldStreet)
      .replace(/<<Address2>>/g, boldCityStateZip)
      .replace(/<<Disclaimer>>/g, disclaimer);
    
    // Show in modal instead of downloading
    setVerbiageModal({
      open: true,
      content: filledContent,
      title: `${dealershipName} - ${templateName}${isCustomTemplate ? ' (Custom)' : ''}`,
      templateName: templateName,
      isEditing: false,
      originalContent: filledContent,
      rawTemplateContent: templateContent // Store raw template
    });
    
  } catch (error) {
    console.error('Error generating verbiage:', error);
    alert(`Error generating verbiage: ${error.message}`);
  } finally {
    setGeneratingVerbiage(false);
  }
};

const startEditingVerbiage = () => {
  setVerbiageModal(prev => ({
    ...prev,
    isEditing: true
  }));
};

const cancelEditingVerbiage = () => {
  setVerbiageModal(prev => ({
    ...prev,
    isEditing: false,
    content: prev.originalContent // Restore original content
  }));
};

const saveEditedVerbiage = async () => {
  const { templateName, content, originalContent } = verbiageModal;
  
  // Check if content was actually changed
  if (content === originalContent) {
    setVerbiageModal(prev => ({ ...prev, isEditing: false }));
    return;
  }
  
  try {
    setSaving(true);
    
    // Check if this is already a custom template or default template
    const isExistingCustomTemplate = client.customTemplates && client.customTemplates[templateName];
    
    let updatedCustomTemplates;
    let finalTemplateName;
    
    if (isExistingCustomTemplate) {
      // If it's already a custom template, just update it
      updatedCustomTemplates = {
        ...client.customTemplates,
        [templateName]: content
      };
      finalTemplateName = templateName;
    } else {
      // If it's a default template, create a custom version with the same name
      updatedCustomTemplates = {
        ...client.customTemplates,
        [templateName]: content
      };
      finalTemplateName = templateName;
    }
    
    // Update local state
    setClient(prev => ({
      ...prev,
      customTemplates: updatedCustomTemplates
    }));
    
    // Update Firebase
    await updateDoc(doc(db, 'clients', id), {
      customTemplates: updatedCustomTemplates
    });
    
    // Update the modal to reflect the saved template
    setVerbiageModal(prev => ({
      ...prev,
      isEditing: false,
      templateName: finalTemplateName,
      title: `${client.name} - ${finalTemplateName} (Custom)`,
      originalContent: content
    }));
    
    // Refresh available templates
    updateAvailableTemplates();
    
    alert('Template updated successfully!');
    
  } catch (error) {
    console.error('Error saving edited verbiage:', error);
    alert('Failed to save edited verbiage');
  } finally {
    setSaving(false);
  }
};


// Auto-bold function for selected text
const applyBoldToSelection = () => {
  const textarea = document.getElementById('verbiage-textarea');
  if (!textarea) return;
  
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  
  if (start === end) {
    alert('Please select text first');
    return;
  }
  
  const selectedText = verbiageModal.content.substring(start, end);
  
  // Use markdown-style markers instead of Unicode
  const boldText = `**${selectedText}**`;
  
  const newContent = 
    verbiageModal.content.substring(0, start) + 
    boldText + 
    verbiageModal.content.substring(end);
  
  setVerbiageModal(prev => ({
    ...prev,
    content: newContent
  }));
  
  // Restore cursor position after the bold text
  setTimeout(() => {
    textarea.focus();
    textarea.setSelectionRange(start + boldText.length, start + boldText.length);
  }, 0);
};

const applySuperscriptToSelection = () => {
  const textarea = document.getElementById('verbiage-textarea');
  if (!textarea) return;

  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;

  if (start === end) {
    alert('Please select text first');
    return;
  }

  const selectedText = verbiageModal.content.substring(start, end);
  const superText = toSuperscriptUnicode(selectedText);

  const newContent =
    verbiageModal.content.substring(0, start) +
    superText +
    verbiageModal.content.substring(end);

  setVerbiageModal(prev => ({
    ...prev,
    content: newContent
  }));

  setTimeout(() => {
    textarea.focus();
    textarea.setSelectionRange(start + superText.length, start + superText.length);
  }, 0);
};

// Template management functions
const saveCustomTemplate = async () => {
  if (!customTemplateModal.templateName.trim() || !customTemplateModal.content.trim()) {
    alert('Please provide both a template name and content');
    return;
  }
  
  setSavingCustomTemplate(true);
  
  try {
    const updatedCustomTemplates = {
      ...client.customTemplates,
      [customTemplateModal.templateName]: customTemplateModal.content
    };
    
    setClient(prev => ({
      ...prev,
      customTemplates: updatedCustomTemplates
    }));
    
    await updateDoc(doc(db, 'clients', id), {
      customTemplates: updatedCustomTemplates
    });
    
    setCustomTemplateModal({
      open: false,
      templateName: '',
      content: '',
      isEditing: false,
      originalTemplateName: null
    });
    
    updateAvailableTemplates();
    alert('Custom template saved successfully!');
    
  } catch (error) {
    console.error('Error saving custom template:', error);
    alert('Failed to save custom template');
  } finally {
    setSavingCustomTemplate(false);
  }
};

const editCustomTemplate = (templateName) => {
  const content = client.customTemplates[templateName];
  setCustomTemplateModal({
    open: true,
    templateName: templateName,
    content: content,
    isEditing: true,
    originalTemplateName: templateName
  });
};

const deleteCustomTemplate = async (templateName) => {
  if (!window.confirm(`Are you sure you want to delete the "${templateName}" template?`)) {
    return;
  }
  
  try {
    const updatedCustomTemplates = { ...client.customTemplates };
    delete updatedCustomTemplates[templateName];
    
    setClient(prev => ({
      ...prev,
      customTemplates: updatedCustomTemplates
    }));
    
    await updateDoc(doc(db, 'clients', id), {
      customTemplates: updatedCustomTemplates
    });
    
    updateAvailableTemplates();
    alert('Custom template deleted successfully!');
    
  } catch (error) {
    console.error('Error deleting custom template:', error);
    alert('Failed to delete custom template');
  }
};

const updateAvailableTemplates = useCallback(() => {
  const defaultTemplates = Object.keys(TEMPLATES);
  const customTemplates = client?.customTemplates ? Object.keys(client.customTemplates) : [];
  setAvailableTemplates([...defaultTemplates, ...customTemplates]);
}, [client?.customTemplates]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(verbiageModal.content);
      // Brief visual feedback
      const originalTitle = verbiageModal.title;
      setVerbiageModal(prev => ({ ...prev, title: 'Copied to Clipboard!' }));
      setTimeout(() => {
        setVerbiageModal(prev => ({ ...prev, title: originalTitle }));
      }, 1000);
    } catch (error) {
      console.error('Failed to copy:', error);
      alert('Failed to copy to clipboard');
    }
  };

    // Load available templates on component mount
useEffect(() => {
  updateAvailableTemplates();
}, [updateAvailableTemplates]);


  useEffect(() => {
    async function fetchData() {
      try {
        const clientId = id;  

        // 1) Load client
        const clientSnap = await getDoc(doc(db, 'clients', clientId));
        if (!clientSnap.exists()) {
          console.warn('No such client:', clientId);
          setLoading(false);
          return;
        }
        const clientData = clientSnap.data();
        setClient({
  ...clientData,
  id: clientSnap.id,
  hours: clientData.hours || defaultHours,
  notes: clientData.notes || '',
  additionalAssets: clientData.additionalAssets || [],
  dealershipWebsite: clientData.dealershipWebsite || '',
  secondaryEmails: clientData.secondaryEmails || [],
  genericDisclaimer: clientData.genericDisclaimer || '',
  specialDisclaimer: clientData.specialDisclaimer || '',
  customTemplates: clientData.customTemplates || {},
  clientDealerAppsUsername: clientData.clientDealerAppsUsername || '',
  clientDealerAppsPassword: clientData.clientDealerAppsPassword || '',
  clientG2PNUsername: clientData.clientG2PNUsername || '',
  clientG2PNPassword: clientData.clientG2PNPassword || ''
});





        // 2) Query orders
        const oQ = query(
          collection(db, 'orders'),
          where('clientId', '==', clientId)
        );
        const oSnap = await getDocs(oQ);
        setOrders(oSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // 3) Query Facebook events
        const eQ = query(
          collection(db, 'facebookEvents'),
          where('clientId', '==', clientId)
        );
        const eSnap = await getDocs(eQ);
        setEvents(eSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id]);

  const handleChange = e => {
    const { name, value } = e.target;

    // if it's one of our hours dropdowns…
    if (name.startsWith('hours.')) {
      // name === "hours.monday.open"  →  [_, dayKey, field] = ["hours","monday","open"]
      const [, dayKey, field] = name.split('.');
      setClient(prev => ({
        ...prev,
        hours: {
          // keep every other day the same
          ...prev.hours,
          [dayKey]: {
            // keep whatever that day already had (open or close)
            ...prev.hours[dayKey],
            // overwrite just the one field (open OR close)
            [field]: value
          }
        }
      }));
    } else {
      // everything else can be shallow
      setClient(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleLogoUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const fileRef = ref(storage, `logos/${id}/${field}_${file.name}`);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      setClient(prev => ({ ...prev, [field]: url }));
      await updateDoc(doc(db, 'clients', id), { [field]: url });
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Upload failed');
    }
  };

  async function handleAdditionalAssetsUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const fileRef = ref(storage, `assets/${id}/${file.name}`);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      const newAsset = {
        id: file.name,
        name: file.name,
        url,
        note: assetNote,
      };

      setClient(prev => ({
        ...prev,
        additionalAssets: [...prev.additionalAssets, newAsset]
      }));

      await updateDoc(doc(db, 'clients', id), {
        additionalAssets: arrayUnion(newAsset)
      });

      setAssetNote('');
    } catch (err) {
      console.error('Asset upload failed:', err);
      alert('Upload failed');
    }
  }

  const handleDeleteAsset = async asset => {
    try {
      setClient(prev => ({
        ...prev,
        additionalAssets: prev.additionalAssets.filter(a => a.id !== asset.id)
      }));

      await updateDoc(doc(db, 'clients', id), {
        additionalAssets: arrayRemove(asset)
      });
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Delete failed');
    }
  };

  const handleVoucherUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const fileRef = ref(storage, `voucherTemplates/${id}/${file.name}`);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      setClient(prev => ({ ...prev, voucherTemplate: url }));
      await updateDoc(doc(db, 'clients', id), { voucherTemplate: url });
    } catch (err) {
      console.error('Voucher upload failed:', err);
      alert('Voucher upload failed');
    }
  };

  const handleVoucher2Upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const fileRef = ref(storage, `voucherTemplates/${id}/voucher2_${file.name}`);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      setClient(prev => ({ ...prev, voucherTemplate2: url }));
      await updateDoc(doc(db, 'clients', id), { voucherTemplate2: url });
    } catch (err) {
      console.error('Voucher 2 upload failed:', err);
      alert('Voucher 2 upload failed');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'clients', id), client);
      setEditMode(false);
      alert('Changes saved!');
      refresh(['clients']);
    } catch (err) {
      console.error('Save error:', err);
      alert('Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Pre-Sale Email ──────────────────────────────────────────────────────────
  // Same mailto pattern as "Send to JSDirect" in ViewOrderPage: builds a plain-text
  // email and opens the user's default mail program. mailto can't attach files, so
  // both vouchers for the chosen event are downloaded first to drag into the email.
  const PRE_SALE_CC = ['will@srmgauto.com', 'ty@srmgauto.com', 'joshs@srmgauto.com'];

  const getPreSaleGreeting = () => {
    // Current time in EST (matches ViewOrderPage getGreeting)
    const now = new Date();
    const estTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const hour = estTime.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const parseEventDate = (value) => {
    if (!value) return null;
    let d;
    if (value?.seconds) d = new Date(value.seconds * 1000);
    else if (typeof value === 'string') d = new Date(`${value}T00:00:00`);
    else d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  // 2026-09-25 -> 9/25/26
  const formatShortDate = (value) => {
    const d = parseEventDate(value);
    if (!d) return 'TBD';
    return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
  };

  // Client values (e.g. passwords with < or &) must be escaped before going into HTML
  const escapeHtml = (str) => String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  // Active (non-archived) events for this client, newest start date first
  const preSaleEvents = [...events]
    .filter(ev => ev.active !== false)
    .sort((a, b) => String(b.startDate || '').localeCompare(String(a.startDate || '')));

  const handleOpenPreSaleEmail = () => {
    setPreSaleEmailModal({
      open: true,
      eventId: preSaleEvents[0]?.id || ''
    });
  };

  const closePreSaleEmailModal = () => {
    setPreSaleEmailModal({ open: false, eventId: '' });
  };

  const handleSendPreSaleEmail = async () => {
    const ev = events.find(e => e.id === preSaleEmailModal.eventId);
    if (!ev) {
      alert('Select an event first');
      return;
    }

    const missing = [];
    if (!client.facebookPageLink) missing.push('Facebook Page Link');
    if (!client.clientDealerAppsUsername) missing.push('DealerApps Username');
    if (!client.clientDealerAppsPassword) missing.push('DealerApps Password');
    if (missing.length > 0 && !window.confirm(`This client is missing: ${missing.join(', ')}.\n\nOpen the email anyway?`)) {
      return;
    }

    const cleanedName = (client.name || '')
      .replace(/\n|\r/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const startDate = parseEventDate(ev.startDate);
    const monthAbbr = startDate
      ? startDate.toLocaleString('en-US', { month: 'short' }).toUpperCase()
      : new Date().toLocaleString('en-US', { month: 'short' }).toUpperCase();

    const fbLink = client.facebookPageLink
      ? (client.facebookPageLink.startsWith('http') ? client.facebookPageLink : `https://${client.facebookPageLink}`)
      : '';

    const recipients = [client.email, ...(client.secondaryEmails || [])]
      .map(e => (e || '').trim())
      .filter(Boolean);

    const subject = `*${monthAbbr}* ${cleanedName} Pre-Sale *SRMG FB*`;

    const body = `${getPreSaleGreeting()},

Below are the details for your upcoming Facebook Campaign:

Event Dates: ${formatShortDate(ev.startDate)} - ${formatShortDate(ev.endDate)}

You can use this link to your Facebook Event Page to view your ads.  Let us know if you see anything you would like adjusted.
${fbLink}

***PLEASE HAVE YOUR TEAM LIKE AND SHARE THE ADS 🙂***


Login Codes for Leads Dashboard

Website: www.srmgleads.com
Username: ${client.clientDealerAppsUsername || ''}
Password: ${client.clientDealerAppsPassword || ''}

CRM Integration (If Applicable): Would you like us to add your CRM email address to our system so leads can be sent directly to your CRM? If so, please provide the email address you would like us to use.

**Copies of the vouchers that customers will receive are attached.  The gold voucher is sent to the customer once they schedule an appointment.  The green, special voucher, will be included with the customer's appointment reminder as an added incentive to help ensure appointment attendance.***

Please let me know if you have any questions. We look forward to a great event with your team!
`;

    // Formatted version of the same body, copied to the clipboard as HTML.
    // mailto can only carry plain text, so the email opens with an empty body
    // and the user pastes (Ctrl+V) to get bold/underline/link formatting.
    const eventDates = `${formatShortDate(ev.startDate)} - ${formatShortDate(ev.endDate)}`;
    const htmlBody = `<div style="font-family: Aptos, Calibri, Arial, sans-serif; font-size: 11pt; color: #000000;">
<p>${escapeHtml(getPreSaleGreeting())},</p>
<p>Below are the details for your upcoming Facebook Campaign:</p>
<p><b>Event Dates:&nbsp; ${escapeHtml(eventDates)}</b></p>
<p><b>You can use this link to your Facebook Event Page to view your ads.&nbsp; Let us know if you see anything you would like adjusted.</b><br>
${fbLink ? `<a href="${escapeHtml(fbLink)}">${escapeHtml(fbLink)}</a>` : ''}</p>
<p><b><i>***PLEASE HAVE YOUR TEAM LIKE AND SHARE THE ADS 🙂***</i></b></p>
<p>&nbsp;</p>
<p><b><u>Login Codes for Leads Dashboard</u></b></p>
<p><b>Website:</b> <a href="https://srmgleads.com">srmgleads.com</a><br>
<b>Username:</b>&nbsp; ${escapeHtml(client.clientDealerAppsUsername)}<br>
<b>Password:</b> ${escapeHtml(client.clientDealerAppsPassword)}</p>
<p><b>CRM Integration (If Applicable):</b> Would you like us to add your CRM email address to our system so leads can be sent directly to your CRM? If so, please provide the email address you would like us to use.</p>
<p><i>**Copies of the vouchers that customers will receive are attached.&nbsp; The gold voucher is sent to the customer once they schedule an appointment.&nbsp; The green, special voucher, will be included with the customer's appointment reminder as an added incentive to help ensure appointment attendance.***</i></p>
<p>Please let me know if you have any questions. We look forward to a great event with your team!</p>
</div>`;

    // Copy first, while the click still counts as a user gesture
    let copiedFormatted = false;
    try {
      if (window.ClipboardItem && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            'text/html': new Blob([htmlBody], { type: 'text/html' }),
            'text/plain': new Blob([body], { type: 'text/plain' })
          })
        ]);
        copiedFormatted = true;
      }
    } catch (err) {
      console.warn('Formatted clipboard copy failed, falling back to plain-text body:', err);
    }

    // Formatted copy worked -> empty body (user pastes). Otherwise -> plain text in the body like before.
    const mailtoLink = `mailto:${recipients.join(',')}` +
      `?cc=${PRE_SALE_CC.join(',')}` +
      `&subject=${encodeURIComponent(subject)}` +
      (copiedFormatted ? '' : `&body=${encodeURIComponent(body)}`);

    // Download both vouchers so they can be dragged into the email
    generateEventVoucher(ev, client);
    generate500Voucher(ev, client);

    closePreSaleEmailModal();

    // Short delay so the voucher downloads start before the mail program takes focus
    setTimeout(() => {
      window.location.href = mailtoLink;
    }, 800);
  };

  const toggleDropdown = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  // Fetch and cache the latest proof URL for a given order
  const fetchProofForOrder = async (orderId) => {
    if (orderProofCache[orderId] !== undefined) return;
    try {
      const snap = await getDocs(collection(db, 'orders', orderId, 'proofs'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const ta = a.uploadedAt?.toDate?.().getTime() || 0;
        const tb = b.uploadedAt?.toDate?.().getTime() || 0;
        return tb - ta;
      });
      const url = list[0]?.fileUrl || list[0]?.url || null;
      setOrderProofCache(prev => ({ ...prev, [orderId]: url }));
    } catch (err) {
      console.error('Error fetching proof for order', orderId, err);
      setOrderProofCache(prev => ({ ...prev, [orderId]: null }));
    }
  };

  if (loading) return <p>Loading...</p>;
  if (!client) return <p>Client not found</p>;
  // Add this as a NEW function (don't replace applyBoldToSelection)
const handleTextChange = (newContent) => {
  setVerbiageModal(prev => ({
    ...prev,
    content: newContent
  }));
};


  return (
    <div className="client-profile-container">
      {/* ─── Row 1: Logo + Chats */}
      <div className="client-top-section">
        {/* ── Logo Section ── */}
        <div className="logo-section">
          {client.blackLogo
            ? <img src={client.blackLogo} alt="Dealership Logo" />
            : (!editMode && <p>No logo</p>)
          }
          
          {/* NEW: Verbiage Generation Buttons */}
{/* Enhanced Verbiage Generation Section */}
<div style={{ marginTop: '20px' }}>
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
    <h5 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>
      Facebook Verbiage
    </h5>
    <button
      onClick={() => setCustomTemplateModal({
        open: true,
        templateName: '',
        content: '',
        isEditing: false,
        originalTemplateName: null
      })}
      style={{
        padding: '4px 8px',
        backgroundColor: '#28a745',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '11px'
      }}
    >
      + Custom
    </button>
  </div>
  
  {availableTemplates.length > 0 && (
    <div style={{ fontSize: '12px' }}>
      <div style={{ marginTop: '8px', maxHeight: '290px', overflowY: 'auto' }}>
        {availableTemplates.map(template => {
          const isCustom = client.customTemplates && client.customTemplates[template];
          return (
            <div key={template} style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
              <button
                onClick={() => generateVerbiage(template)}
                disabled={generatingVerbiage || !client.name}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  backgroundColor: isCustom ? '#6f42c1' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  textAlign: 'left',
                  marginRight: isCustom ? '4px' : '0'
                }}
              >
                {template.replace('.txt', '')} {isCustom && '(Custom)'}
              </button>
              {isCustom && (
                <>
                  <button
                    onClick={() => editCustomTemplate(template)}
                    style={{
                      padding: '4px 6px',
                      backgroundColor: '#ffc107',
                      color: 'black',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '10px',
                      marginRight: '4px'
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteCustomTemplate(template)}
                    style={{
                      padding: '4px 6px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '10px'
                    }}
                  >
                    Del
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  )}
  
  {!client.name && (
    <p style={{ fontSize: '11px', color: '#dc3545', margin: '8px 0 0 0' }}>
      Client name required for verbiage generation
    </p>
  )}
</div>

        </div>

        

        {/* ── Messages Section ── */}
<div className="messages-section">
  {/* ── Client Chat - General Only ── */}
  <div className="message-column">
    <h3>General Client Messages</h3>
    <MessageForm
      clientId={id}
      recipientType="client"
      // No orderId passed = general client messages only
    />
    <MessageLog
      key={`client-general-${id}`}
      clientId={id}
      recipientType="client"
      showOrderMessages={false}  // Only show non-order messages
      clearFlag={clearClientLog}
    />
    <div className="client-actions">
      <button
        style={{ marginBottom: 8 }}
        onClick={() => setClearClientLog(n => n + 1)}
      >
        Clear General Chat
      </button>
    </div>
  </div>

  {/* ── Internal Chat - General Only ── */}
  <div className="message-column">
    <h3>Internal Client Notes</h3>
    <MessageForm
      clientId={id}
      recipientType="internal"
      // No orderId passed = general internal messages only
    />
    <MessageLog
      key={`internal-general-${id}`}
      clientId={id}
      recipientType="internal"
      showOrderMessages={false}  // Only show non-order messages
      clearFlag={clearInternalLog}
    />
    <div className="client-actions">
      <button
        style={{ marginBottom: 8 }}
        onClick={() => setClearInternalLog(n => n + 1)}
      >
        Clear Internal Chat
      </button>
    </div>
  </div>
</div>
      </div>

      {/* ─── Row 2: Three Info Cards */}
      <div className="client-info-section">
        {/* General */}
        <div className="info-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ marginBottom: 2 }}>{client.name}</h3>
              {client.name && <CopyButton text={client.name} />}
            </div>
            <div>
              <div className="client-actions">
                <button onClick={() => setEditMode(!editMode)}>
                  {editMode ? 'Cancel' : 'Edit Client'}
                </button>
              </div>
              <div className="client-actions">
                {editMode && (
                  <button onClick={handleSave} disabled={saving} style={{ marginBottom: 8, marginLeft: 8 }}>
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {editMode ? (
            <>            
              <input name="name" value={client.name} onChange={handleChange} placeholder="Name" style={{ width: '100%', marginBottom: 8 }} />
              <input name="generalManager" value={client.generalManager || ''} onChange={handleChange} placeholder="General Manager" style={{ width: '100%', marginBottom: 8 }} />
              <input name="phone" value={client.phone || ''} onChange={handleChange} placeholder="Primary Phone" style={{ width: '100%', marginBottom: 8 }} />
              <input name="phone2" value={client.phone2 || ''} onChange={handleChange} placeholder="Secondary Phone" style={{ width: '100%', marginBottom: 8 }} />
              
              {/* Email Inputs */}
              <input
                name="email"
                value={client.email || ''}
                onChange={handleChange}
                placeholder="Primary Email"
                style={{ width: '100%', marginBottom: 8 }}
              />

              {client.secondaryEmails.map((email, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <input
                    value={email}
                    onChange={(e) => {
                      const updated = [...client.secondaryEmails];
                      updated[index] = e.target.value;
                      setClient(prev => ({ ...prev, secondaryEmails: updated }));
                    }}
                    placeholder={`Secondary Email ${index + 1}`}
                    style={{ flex: 1, marginRight: 8 }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const updated = client.secondaryEmails.filter((_, i) => i !== index);
                      setClient(prev => ({ ...prev, secondaryEmails: updated }));
                    }}
                    style={{
                      padding: '4px 10px',
                      backgroundColor: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Delete
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={() =>
                  setClient(prev => ({
                    ...prev,
                    secondaryEmails: [...(prev.secondaryEmails || []), '']
                  }))
                }
                style={{
                  marginBottom: 8,
                  backgroundColor: '#003cff',
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                + Add Email
              </button>

              <input
  name="accountingEmail"
  value={client.accountingEmail || ''}
  onChange={handleChange}
  placeholder="Accounting Email"
  style={{ width: '100%', marginBottom: 8 }}
/>

              <input
                name="password"
                value={client.password || ''}
                onChange={handleChange}
                placeholder="Client Password"
                style={{ width: '100%', marginBottom: 8 }}
              />

              <select name="manufacturer" value={client.manufacturer || ''} onChange={handleChange} style={{ width: '100%', marginBottom: 8 }}>
                <option value="">Select Manufacturer</option>
                <option>Acura</option><option>Audi</option><option>BMW</option><option>Buick</option><option>Cadillac</option>
                <option>Chevrolet</option><option>CDJR</option><option>Ford</option><option>CDJR Ford</option><option>Ford Nissan</option><option>GMC</option>
                <option>Honda</option><option>Hyundai</option><option>Infiniti</option><option>Kia</option>
                <option>Lexus</option><option>Lincoln</option><option>Mazda</option><option>Mercedes-Benz</option>
                <option>Mitsubishi</option><option>Nissan</option><option>Mitsubishi Nissan</option><option>Nissan Kia</option><option>Subaru</option><option>Tesla</option>
                <option>Toyota</option><option>Used Vehicles</option><option>Volkswagen</option><option>Volvo</option><option>Other</option>
              </select>

              {/* FTC Compliance settings */}
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px', marginBottom: 8, background: '#f9fafb' }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>FTC Compliance</div>
                <select name="complianceTier" value={client.complianceTier || 'standard'} onChange={handleChange} style={{ width: '100%', marginBottom: 8 }}>
                  <option value="standard">Standard Compliance (default)</option>
                  <option value="medium">Medium Compliance (checklist + math checks, standard disclaimer)</option>
                  <option value="ftc-enhanced">FTC-Enhanced Compliance (Morgan Auto Group, etc.)</option>
                </select>
                <input
                  name="autoGroup"
                  value={client.autoGroup || ''}
                  onChange={handleChange}
                  placeholder="Auto Group (e.g. Morgan Auto Group)"
                  style={{ width: '100%', marginBottom: 8 }}
                />
                {(client.complianceTier === 'ftc-enhanced') && (
                  <>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                      Mandatory dealer fees (written into every FTC-enhanced disclaimer - enter numbers only):
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                      <input name="preDeliveryFee" value={client.preDeliveryFee || ''} onChange={handleChange} placeholder="Pre-Delivery Fee (e.g. 1298)" style={{ flex: '1 1 45%' }} />
                      <input name="privateTagFee" value={client.privateTagFee || ''} onChange={handleChange} placeholder="Private Tag Fee (e.g. 189)" style={{ flex: '1 1 45%' }} />
                      <input name="electronicFilingFee" value={client.electronicFilingFee || ''} onChange={handleChange} placeholder="E-Filing Fee (e.g. 598)" style={{ flex: '1 1 45%' }} />
                      <input name="docFee" value={client.docFee || ''} onChange={handleChange} placeholder="Dealer/Doc Fee (if separate)" style={{ flex: '1 1 45%' }} />
                    </div>
                  </>
                )}
              </div>

              <input
                type="text"
                name="dealershipWebsite"
                value={client.dealershipWebsite}
                onChange={handleChange}
                placeholder="Dealership Website"
                style={{ width: '100%', marginBottom: 8 }}
              />

              <h4>Address:</h4>
              <input name="street1" value={client.street1 || ''} onChange={handleChange} placeholder="Street 1" style={{ width: '100%', marginBottom: 8 }} />
              <input name="street2" value={client.street2 || ''} onChange={handleChange} placeholder="Street 2" style={{ width: '100%', marginBottom: 8 }} />
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input name="city" value={client.city || ''} onChange={handleChange} placeholder="City" style={{ flex: 1 }} />
                <input name="state" value={client.state || ''} onChange={handleChange} placeholder="State" style={{ flex: 1 }} />
                <input name="zip" value={client.zip || ''} onChange={handleChange} placeholder="ZIP" style={{ width: 100 }} />
              </div>

              <input
                name="disclaimerLink"
                value={client.disclaimerLink || ''}
                onChange={handleChange}
                placeholder="Facebook Verbiage Disclaimer Link (https://...)"
                style={{ width: '100%', marginBottom: 8}}
              />

              <input
                name="facebookPageLink"
                value={client.facebookPageLink || ''}
                onChange={handleChange}
                placeholder="Facebook Page Link (https://...)"
                style={{ width: '100%', marginBottom: 8}}
              />

              <input
                name="dealerAppsCode"
                value={client.dealerAppsCode || ''}
                onChange={handleChange}
                placeholder="DealerApps Code"
                style={{ width: '100%', marginBottom: 8 }}
              />

              <input
                name="clientDealerAppsUsername"
                value={client.clientDealerAppsUsername || ''}
                onChange={handleChange}
                placeholder="Client DealerApps Username"
                style={{ width: '100%', marginBottom: 8 }}
              />

              <input
                name="clientDealerAppsPassword"
                value={client.clientDealerAppsPassword || ''}
                onChange={handleChange}
                placeholder="Client DealerApps Password"
                style={{ width: '100%', marginBottom: 8 }}
              />

              <input
                name="clientG2PNUsername"
                value={client.clientG2PNUsername || ''}
                onChange={handleChange}
                placeholder="G2PN Username"
                style={{ width: '100%', marginBottom: 8 }}
              />

              <input
                name="clientG2PNPassword"
                value={client.clientG2PNPassword || ''}
                onChange={handleChange}
                placeholder="G2PN Password"
                style={{ width: '100%', marginBottom: 8 }}
              />
            </>
          ) : (
            <>
              <p><strong>GM:</strong> {client.generalManager}{client.generalManager && <CopyButton text={client.generalManager} />}</p>
              <p><strong>Phone:</strong> {client.phone}{client.phone && <CopyButton text={client.phone} />}</p>
              {client.phone2 && <p><strong>Secondary Phone:</strong> {client.phone2}<CopyButton text={client.phone2} /></p>}
              <p><strong>Email:</strong> {client.email}{client.email && <CopyButton text={client.email} />}</p>
            
              {client.secondaryEmails?.length > 0 && (
                <>
                  {client.secondaryEmails.map((email, idx) => (
                    <p key={idx}><strong>Secondary Email {idx + 1}:</strong> {email}{email && <CopyButton text={email} />}</p>
                  ))}
                </>
              )}

              <p><strong>Accounting Email:</strong> {client.accountingEmail || <em>None set</em>}{client.accountingEmail && <CopyButton text={client.accountingEmail} />}</p>

              <p><strong>Password:</strong> {client.password || <em>None set</em>}</p>
              <p><strong>Manufacturer:</strong> {client.manufacturer}</p>
              <p>
                <strong>Compliance:</strong>{' '}
                {client.complianceTier === 'ftc-enhanced' ? (
                  <span style={{
                    background: '#7f1d1d',
                    color: 'white',
                    padding: '2px 10px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 700
                  }}>
                    🛡️ FTC-ENHANCED{client.autoGroup ? ` - ${client.autoGroup}` : ''}
                  </span>
                ) : client.complianceTier === 'medium' ? (
                  <span style={{
                    background: '#b45309',
                    color: 'white',
                    padding: '2px 10px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 700
                  }}>
                    ⚠️ MEDIUM{client.autoGroup ? ` - ${client.autoGroup}` : ''}
                  </span>
                ) : (
                  <span>Standard</span>
                )}
              </p>

              {client.dealershipWebsite
                ? (
                   <p>
                    <strong>Website:</strong>{' '}
                    <a
                  href={
                    client.dealershipWebsite.startsWith('http')
                      ? client.dealershipWebsite
                      : `https://${client.dealershipWebsite}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => {
                    e.stopPropagation(); // ← this is the magic line
                  }}
                  style={{
                    display: 'inline-block',  // optional styling
                    textDecoration: 'underline',
                    color: 'blue',
                    marginTop: 4
                  }}
                >
                  {client.dealershipWebsite}
                </a>
                <CopyButton text={client.dealershipWebsite} />
                  </p>
                )
                : <p><strong>Website:</strong> <em>No website</em></p>
              }

              <p> <strong>Address: </strong>
                {client.street1}
                {client.street2 && `, ${client.street2}`}
                {client.city && `, ${client.city}`}
                {client.state && `, ${client.state}`}
                {client.zip && ` ${client.zip}`}
                {client.street1 && <CopyButton text={[client.street1, client.street2, client.city, client.state, client.zip].filter(Boolean).join(', ')} />}
              </p>

              <p>
                <strong>Facebook Verbiage Disclaimer Link:</strong>{' '}
                {client.disclaimerLink ? (
                  <>
                    <a
                      href={
                        client.disclaimerLink.startsWith('http')
                          ? client.disclaimerLink
                          : `https://${client.disclaimerLink}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {client.disclaimerLink}
                    </a>
                    <CopyButton text={client.disclaimerLink} />
                  </>
                ) : (
                  <em>None set</em>
                )}
              </p>

              <p>
                <strong>Facebook Page Link:</strong>{' '}
                {client.facebookPageLink ? (
                  <>
                    <a
                      href={
                        client.facebookPageLink.startsWith('http')
                          ? client.facebookPageLink
                          : `https://${client.facebookPageLink}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {client.facebookPageLink}
                    </a>
                    <CopyButton text={client.facebookPageLink} />
                  </>
                ) : (
                  <em>None set</em>
                )}
              </p>

              <p><strong>DealerApps Code:</strong> {client.dealerAppsCode || <em>None set</em>}</p>
              
              <div style={{ 
                marginTop: '16px', 
                padding: '12px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '6px',
                border: '1px solid #dee2e6'
              }}>
                <p style={{ marginTop: 0 }}>
                  <strong>Client DealerApps Username:</strong> {client.clientDealerAppsUsername || <em>None set</em>}
                </p>
                <p style={{ marginBottom: 0 }}>
                  <strong>Client DealerApps Password:</strong> {client.clientDealerAppsPassword || <em>None set</em>}
                </p>
                <button
                  onClick={handleOpenPreSaleEmail}
                  disabled={!client.email || preSaleEvents.length === 0}
                  style={{
                    marginTop: '12px',
                    padding: '8px 16px',
                    backgroundColor: !client.email || preSaleEvents.length === 0 ? '#6c757d' : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: !client.email || preSaleEvents.length === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    width: '100%'
                  }}
                  title={
                    !client.email
                      ? 'Please set a primary email first'
                      : preSaleEvents.length === 0
                        ? 'This client has no active Facebook events'
                        : 'Open a pre-sale email in your mail program'
                  }
                >
                  Send Pre-Sale Email
                </button>
              </div>

              <div style={{ 
                marginTop: '16px', 
                padding: '12px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '6px',
                border: '1px solid #dee2e6'
              }}>
                <p style={{ marginTop: 0 }}>
                  <strong>G2PN Username:</strong> {client.clientG2PNUsername || <em>None set</em>}
                </p>
                <p style={{ marginBottom: 0 }}>
                  <strong>G2PN Password:</strong> {client.clientG2PNPassword || <em>None set</em>}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Hours */}
        <div className="info-card">
          <h4>Hours</h4>
          {editMode ? (
            daysOfWeek.map(day => {
              const key = day.toLowerCase();
              const open = client.hours?.[key]?.open || '';
              const close = client.hours?.[key]?.close || '';
              return (
                <div key={day} style={{ marginBottom: 8 }}>
                  <label style={{ width: 80, display: 'inline-block' }}>{day}:</label>
                  <select
                    name={`hours.${key}.open`}
                    value={open}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    <option value="Closed">Closed</option>
                    {timeOptions.map(t => <option key={t}>{t}</option>)}
                  </select>
                  {open !== 'Closed' && open && (
                    <span style={{ marginLeft: 8 }}>
                      to <select
                        name={`hours.${key}.close`}
                        value={close}
                        onChange={handleChange}
                      >
                        <option value="">Select</option>
                        {timeOptions.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </span>
                  )}
                </div>
              );
            })
          ) : (
            daysOfWeek.map(day => {
              const key = day.toLowerCase();
              const open = client.hours?.[key]?.open;
              const close = client.hours?.[key]?.close;
              return (
                <p key={day}><strong>{day}:</strong> {(!open || open === 'Closed') ? 'Closed' : `${open} – ${close}`}</p>
              );
            })
          )}
          
          {/* Notes */}
          <h4>Notes</h4>
          {editMode ? (
            <textarea
              name="notes"
              value={client.notes}
              onChange={handleChange}
              placeholder="Add notes here…"
              style={{
                width: '100%',
                minHeight: 80,
                marginBottom: 8
              }}
            />
          ) : (
            <p>
              {client.notes
                ? <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{client.notes}</pre>
                : <em>No notes yet</em>
              }
            </p>
          )}
        </div>

        {/* Assets */}
        <div className="info-card">
          <h4>Logos & Assets</h4>
          <div>
            {editMode ? (
              <>
                {/* Black Logo Replacement */}
                <p>
                  Black Logo: {client.blackLogo
                    ? <button className="link-button" onClick={() => handleForceDownload(client.blackLogo, `${client.name}_black_logo`)}>Download</button>
                    : 'None'
                  }
                </p>
                <input
                  type="file"
                  onChange={e => handleLogoUpload(e, 'blackLogo')}
                  style={{ marginBottom: 8 }}
                />

                {/* White Logo Replacement */}
                <p>
                  White Logo: {client.whiteLogo
                    ? <button className="link-button" onClick={() => handleForceDownload(client.whiteLogo, `${client.name}_white_logo`)}>Download</button>
                    : 'None'
                  }
                </p>
                <input
                  type="file"
                  onChange={e => handleLogoUpload(e, 'whiteLogo')}
                />
              </>
            ) : (
              <>
                {client.blackLogo
                  ? <p><button className="link-button" onClick={() => handleForceDownload(client.blackLogo, `${client.name}_black_logo`)}>Download Black Logo</button></p>
                  : <p>No Black logo</p>
                }
                {client.whiteLogo
                  ? <p><button className="link-button" onClick={() => handleForceDownload(client.whiteLogo, `${client.name}_white_logo`)}>Download White Logo</button></p>
                  : <p>No White logo</p>
                }
              </>
            )}
          </div>

          

          {/* Voucher Template */}
          <p>
            <strong>Voucher Template:</strong>{' '}
            {client.voucherTemplate ? (
              <button
                className="link-button"
                onClick={() => handleForceDownload(client.voucherTemplate, `${client.name}_voucher_template`)}
              >
                Download Voucher
              </button>
            ) : (
              'None'
            )}
          </p>

          {/* File Upload (Only Show in Edit Mode) */}
          {editMode && (
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleVoucherUpload}
            />
          )}

          {/* Voucher Template 2 */}
          <p>
            <strong>$500 Off Voucher:</strong>{' '}
            {client.voucherTemplate2 ? (
              <button
                className="link-button"
                onClick={() => handleForceDownload(client.voucherTemplate2, `${client.name}_500_voucher`)}
              >
                Download Voucher
              </button>
            ) : (
              'None'
            )}
          </p>

          {/* File Upload for Voucher 2 (Only Show in Edit Mode) */}
          {editMode && (
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleVoucher2Upload}
            />
          )}

          <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
  




          <div className="additional-assets">
            <h5>Additional Assets</h5>
            {editMode && (
              <>
                <textarea placeholder="Add a short note…" value={assetNote} onChange={e => setAssetNote(e.target.value)} />
                <input type="file" onChange={handleAdditionalAssetsUpload} />
              </>
            )}
            <ul>
              {client.additionalAssets.map(asset => (
                <li key={asset.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <button className="link-button" onClick={() => handleForceDownload(asset.url, asset.name)}>{asset.name}</button>
                    {asset.note && <div><em>{asset.note}</em></div>}
                  </div>
                  {editMode && <button onClick={() => handleDeleteAsset(asset)}>Delete</button>}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <h5>Disclaimer Generation</h5>
  
  {/* Generic Disclaimer */}
  <div style={{ marginBottom: '15px' }}>
    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
      Generic Disclaimer
    </label>
    {editMode ? (
          <div>
      <textarea
        value={client.genericDisclaimer}
        onChange={(e) => handleDisclaimerChange('genericDisclaimer', e.target.value)}
        placeholder="Enter generic disclaimer text..."
        style={{
          width: '100%',
          minHeight: '100px',
          marginBottom: '8px',
          padding: '8px',
          border: '1px solid #ddd',
          borderRadius: '4px'
        }}
      />
      <button
        onClick={() => generateDisclaimer('generic', client.genericDisclaimer)}
        disabled={generatingDisclaimer || !client.genericDisclaimer.trim()}
        style={{
          padding: '6px 12px',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '12px'
        }}
      >
        {generatingDisclaimer ? 'Generating...' : 'Generate Generic Disclaimer'}
      </button>
      <button
        onClick={() => handleDisclaimerChange('genericDisclaimer', FB_GENERIC_DISCLAIMER)}
        style={{
          padding: '6px 12px',
          marginLeft: '8px',
          backgroundColor: '#7c3aed',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '12px'
        }}
        title="Fill with the standard compliant generic disclaimer text"
      >
        Insert Standard Text
      </button>
    </div>
  ) : (
    <div>
      {client.genericDisclaimer ? (
        <div>
          <details style={{ marginBottom: '8px' }}>
            <summary style={{ 
              cursor: 'pointer', 
              padding: '8px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '4px',
              userSelect: 'none'
            }}>
              Click to view disclaimer
            </summary>
            <p style={{ 
              margin: '8px 0', 
              padding: '12px', 
              backgroundColor: '#ffffff', 
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              maxHeight: '200px',
              overflowY: 'auto',
              whiteSpace: 'pre-wrap'
            }}>
              {client.genericDisclaimer}
            </p>
          </details>
          <button
            onClick={() => generateDisclaimer('generic', client.genericDisclaimer)}
            disabled={generatingDisclaimer}
            style={{
              padding: '6px 12px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            {generatingDisclaimer ? 'Generating...' : 'Generate Generic Disclaimer'}
          </button>
        </div>
      ) : (
        <p style={{ margin: '8px 0', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
          <em>No generic disclaimer text set</em>
        </p>
      )}
      {client.genericDisclaimerLink && (
        <div style={{ marginTop: '8px' }}>
          <strong>Saved Link:</strong>{' '}
          <a href={`https://${client.genericDisclaimerLink}`} target="_blank" rel="noopener noreferrer">
            {client.genericDisclaimerLink}
          </a>
        </div>
      )}
    </div>
  )}
</div>


  
  {/* Special/Vehicle Disclaimer */}
  <div style={{ marginBottom: '15px' }}>
    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
      Special/Vehicle Disclaimer
    </label>
    {editMode ? (
      <div>
      <textarea
        value={client.specialDisclaimer}
        onChange={(e) => handleDisclaimerChange('specialDisclaimer', e.target.value)}
        placeholder="Enter special/vehicle disclaimer text..."
        style={{
          width: '100%',
          minHeight: '100px',
          marginBottom: '8px',
          padding: '8px',
          border: '1px solid #ddd',
          borderRadius: '4px'
        }}
      />
      <button
        onClick={() => generateDisclaimer('special', client.specialDisclaimer)}
        disabled={generatingDisclaimer || !client.specialDisclaimer.trim()}
        style={{
          padding: '6px 12px',
          backgroundColor: '#28a745',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '12px'
        }}
      >
        {generatingDisclaimer ? 'Generating...' : 'Generate Special Disclaimer'}
      </button>
      <button
        onClick={() => setFbBuilderOpen(true)}
        style={{
          padding: '6px 12px',
          marginLeft: '8px',
          backgroundColor: '#7c3aed',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '12px'
        }}
        title="Assemble a compliant disclaimer from offer blocks with verified math"
      >
        🔧 Build Compliant Disclaimer
      </button>
      <FbDisclaimerBuilder
        open={fbBuilderOpen}
        onClose={() => setFbBuilderOpen(false)}
        client={client}
        onInsert={(text) => handleDisclaimerChange('specialDisclaimer', text)}
      />
    </div>
  ) : (
    <div>
      {client.specialDisclaimer ? (
        <div>
          <details style={{ marginBottom: '8px' }}>
            <summary style={{ 
              cursor: 'pointer', 
              padding: '8px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '4px',
              userSelect: 'none'
            }}>
              Click to view disclaimer
            </summary>
            <p style={{ 
              margin: '8px 0', 
              padding: '12px', 
              backgroundColor: '#ffffff', 
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              maxHeight: '200px',
              overflowY: 'auto',
              whiteSpace: 'pre-wrap'
            }}>
              {client.specialDisclaimer}
            </p>
          </details>
          <button
            onClick={() => generateDisclaimer('special', client.specialDisclaimer)}
            disabled={generatingDisclaimer}
            style={{
              padding: '6px 12px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            {generatingDisclaimer ? 'Generating...' : 'Generate Special Disclaimer'}
          </button>
        </div>
      ) : (
        <p style={{ margin: '8px 0', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
          <em>No special disclaimer text set</em>
        </p>
      )}
      {client.specialDisclaimerLink && (
        <div style={{ marginTop: '8px' }}>
          <strong>Saved Link:</strong>{' '}
          <a href={`https://${client.specialDisclaimerLink}`} target="_blank" rel="noopener noreferrer">
            {client.specialDisclaimerLink}
          </a>
        </div>
      )}
    </div>
  )}
</div>
</div>
      </div>

      {/* ─── Row 3: IMPROVED DROPDOWNS FOR Facebook, Direct Mail, Analytics */}
      <div className="client-extra-section">
        <div className="info-card full-width">
          <h4>Client Data</h4>
          
          {/* Direct Mail Orders Dropdown */}
          <div className="dropdown-section">
            <button 
              className="dropdown-header"
              onClick={() => toggleDropdown('orders')}
            >
              <span>Direct Mail Orders ({orders.length})</span>
              {activeDropdown === 'orders' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {activeDropdown === 'orders' && (
              <div className="dropdown-content">
                {orders.length === 0 ? (
                  <p>No Direct Mail Orders</p>
                ) : (
                  <div style={{ padding: 0 }}>
                    {(() => {
                      // Helper function to check if order has any currently active drops
                      const isOrderLive = (order) => {
                        if (!order.drops || order.drops.length === 0) return false;
                        const now = new Date();
                        return order.drops.some(drop => {
                          if (!drop.startDate || !drop.endDate) return false;
                          const startDate = new Date(drop.startDate);
                          const endDate = new Date(drop.endDate);
                          return now >= startDate && now <= endDate;
                        });
                      };

                      // Get the earliest drop date for sorting
                      const getEarliestDropDate = (order) => {
                        if (!order.drops || order.drops.length === 0) {
                          return order.createdAt?.toDate?.() || new Date(0);
                        }
                        const dates = order.drops
                          .filter(drop => drop.startDate)
                          .map(drop => new Date(drop.startDate));
                        return dates.length > 0 ? new Date(Math.min(...dates)) : order.createdAt?.toDate?.() || new Date(0);
                      };

                      // Sort orders by earliest drop date (most recent first)
                      const sortedOrders = [...orders].sort((a, b) => {
                        const dateA = getEarliestDropDate(a);
                        const dateB = getEarliestDropDate(b);
                        return dateB - dateA;
                      });

                      // Group orders by month (using earliest drop date)
                      const ordersByMonth = {};
                      sortedOrders.forEach(ord => {
                        const sortDate = getEarliestDropDate(ord);
                        const monthYear = sortDate.toLocaleDateString('en-US', { 
                          month: 'long', 
                          year: 'numeric' 
                        });
                        
                        if (!ordersByMonth[monthYear]) {
                          ordersByMonth[monthYear] = [];
                        }
                        ordersByMonth[monthYear].push(ord);
                      });

                      // Render grouped orders
                      return Object.entries(ordersByMonth).map(([monthYear, monthOrders]) => (
                        <div key={monthYear} style={{ marginBottom: 20 }}>
                          {/* Month Header */}
                          <div style={{
                            backgroundColor: '#f0f0f0',
                            padding: '8px 12px',
                            borderRadius: 4,
                            marginBottom: 8,
                            fontWeight: 'bold',
                            fontSize: '14px',
                            color: '#333'
                          }}>
                            {monthYear}
                          </div>
                          
                          {/* Orders for this month */}
                          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {monthOrders.map(ord => {
                              const isLive = isOrderLive(ord);
                              // Trigger proof fetch when this order is rendered
                              if (orderProofCache[ord.id] === undefined) {
                                fetchProofForOrder(ord.id);
                              }
                              const proofUrl = orderProofCache[ord.id] || null;
                              const proofIsPdf = proofUrl && proofUrl.split('?')[0].toLowerCase().endsWith('.pdf');
                              return (
                                <li 
                                  key={ord.id} 
                                  style={{ 
                                    marginBottom: 12, 
                                    padding: 12, 
                                    border: isLive ? '2px solid #28a745' : '1px solid #eee',
                                    borderRadius: 6,
                                    backgroundColor: isLive ? '#d4edda' : 'transparent',
                                    position: 'relative',
                                    display: 'flex',
                                    gap: 12,
                                    alignItems: 'flex-start',
                                  }}
                                >
                                  {/* Proof thumbnail + open link */}
                                  <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                                    <div style={{
                                      width: 110,
                                      height: 80,
                                      borderRadius: 4,
                                      border: '1px solid #ddd',
                                      overflow: 'hidden',
                                      backgroundColor: '#f8f9fa',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}>
                                      {proofUrl === undefined ? (
                                        <span style={{ fontSize: 11, color: '#aaa' }}>Loading…</span>
                                      ) : proofUrl === null ? (
                                        <span style={{ fontSize: 11, color: '#aaa' }}>No proof</span>
                                      ) : proofIsPdf ? (
                                        <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
                                          <iframe
                                            src={`${proofUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                                            title="Mailer proof"
                                            style={{
                                              position: 'absolute',
                                              top: 0,
                                              left: 0,
                                              width: '800px',
                                              height: '600px',
                                              border: 'none',
                                              pointerEvents: 'none',
                                              transform: 'scale(0.1375)',
                                              transformOrigin: 'top left',
                                            }}
                                          />
                                        </div>
                                      ) : (
                                        <img
                                          src={proofUrl}
                                          alt="Mailer proof"
                                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                        />
                                      )}
                                    </div>
                                    {proofUrl && (
                                      <a
                                        href={proofUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          fontSize: 11,
                                          color: '#007bff',
                                          textDecoration: 'none',
                                          textAlign: 'center',
                                        }}
                                      >
                                        Open Full Size
                                      </a>
                                    )}
                                  </div>

                                  {/* Order info */}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    {isLive && (
                                      <div style={{
                                        display: 'inline-block',
                                        marginBottom: 6,
                                        backgroundColor: '#28a745',
                                        color: 'white',
                                        padding: '2px 8px',
                                        borderRadius: 4,
                                        fontSize: '11px',
                                        fontWeight: 'bold'
                                      }}>
                                        LIVE NOW
                                      </div>
                                    )}
                                    <p style={{ marginTop: 0, marginBottom: 8 }}>
                                      <strong>{ord.jobNumber || ord.id}</strong>
                                      {ord.status && (
                                        <span style={{ 
                                          marginLeft: '8px', 
                                          fontSize: '12px',
                                          padding: '2px 6px',
                                          backgroundColor: '#f8f9fa',
                                          borderRadius: 3,
                                          color: '#666'
                                        }}>
                                          {ord.status}
                                        </span>
                                      )}
                                    </p>
                                    {ord.name && (
                                      <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>
                                        {ord.name}
                                      </p>
                                    )}
                                    {ord.campaignName && (
                                      <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>
                                        {ord.campaignName === 'Other' ? (ord.campaignNameOther || 'Other') : ord.campaignName}
                                      </p>
                                    )}
                                    {ord.drops && ord.drops.length > 0 && (
                                      <div style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>
                                        {ord.drops.map((drop, di) => (
                                          <div key={di}>
                                            Drop {di + 1}: {drop.startDate
                                              ? new Date(drop.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                              : 'TBD'}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    <Link to={`/orders/${ord.id}`}>
                                      <button style={{
                                        marginTop: 8,
                                        padding: '4px 12px',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: 4,
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                      }}>
                                        View
                                      </button>
                                    </Link>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Facebook Events Dropdown */}
          <div className="dropdown-section">
            <button 
              className="dropdown-header"
              onClick={() => toggleDropdown('events')}
            >
              <span>Facebook Events ({events.length})</span>
              {activeDropdown === 'events' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {activeDropdown === 'events' && (
              <div className="dropdown-content">
                {events.length === 0 ? (
                  <p>No Facebook Events</p>
                ) : (
                  <div style={{ padding: 0 }}>
                    {(() => {
                      // Helper function to check if event is currently live
                      const isEventLive = (event) => {
                        const now = new Date();
                        const liveDate = new Date(event.liveDate);
                        const endDate = new Date(event.endDate);
                        return now >= liveDate && now <= endDate;
                      };

                      // Sort events by liveDate (most recent first)
                      const sortedEvents = [...events].sort((a, b) => {
                        const dateA = new Date(a.liveDate);
                        const dateB = new Date(b.liveDate);
                        return dateB - dateA;
                      });

                      // Group events by month
                      const eventsByMonth = {};
                      sortedEvents.forEach(ev => {
                        const liveDate = new Date(ev.liveDate);
                        const monthYear = liveDate.toLocaleDateString('en-US', { 
                          month: 'long', 
                          year: 'numeric' 
                        });
                        
                        if (!eventsByMonth[monthYear]) {
                          eventsByMonth[monthYear] = [];
                        }
                        eventsByMonth[monthYear].push(ev);
                      });

                      // Render grouped events
                      return Object.entries(eventsByMonth).map(([monthYear, monthEvents]) => (
                        <div key={monthYear} style={{ marginBottom: 20 }}>
                          {/* Month Header */}
                          <div style={{
                            backgroundColor: '#f0f0f0',
                            padding: '8px 12px',
                            borderRadius: 4,
                            marginBottom: 8,
                            fontWeight: 'bold',
                            fontSize: '14px',
                            color: '#333'
                          }}>
                            {monthYear}
                          </div>
                          
                          {/* Events for this month */}
                          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {monthEvents.map(ev => {
                              const isLive = isEventLive(ev);
                              // Match FacebookEventsPage: archived events (active === false) can't generate vouchers
                              const isArchived = ev.active === false;
                              return (
                                <li 
                                  key={ev.id} 
                                  style={{ 
                                    marginBottom: 12, 
                                    padding: 12, 
                                    border: isLive ? '2px solid #28a745' : '1px solid #eee',
                                    borderRadius: 6,
                                    backgroundColor: isLive ? '#d4edda' : 'transparent',
                                    position: 'relative',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-end',
                                    gap: 12,
                                    flexWrap: 'wrap'
                                  }}
                                >
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                  {isLive && (
                                    <div style={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 8,
                                      backgroundColor: '#28a745',
                                      color: 'white',
                                      padding: '2px 8px',
                                      borderRadius: 4,
                                      fontSize: '11px',
                                      fontWeight: 'bold'
                                    }}>
                                      LIVE NOW
                                    </div>
                                  )}
                                  <p style={{ marginTop: 0, marginBottom: 8 }}>
                                    <strong>{ev.jobName || ev.id}</strong>
                                  </p>
                                  <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>
                                    {ev.startDate} → {ev.endDate}
                                  </p>
                                  <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>
                                    Live: {ev.liveDate}
                                  </p>
                                  {ev.voucherUrl && (
                                    <button
                                      onClick={() => handleForceDownload(ev.voucherUrl, `${ev.jobName || ev.id}_voucher`)}
                                      style={{
                                        marginTop: 8,
                                        padding: '4px 12px',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: 4,
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                      }}>
                                      Voucher
                                    </button>
                                  )}
                                  </div>

                                  {/* Voucher downloads - same generators as the Facebook Events page */}
                                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                    <button
                                      onClick={() => generateEventVoucher(ev, client)}
                                      disabled={isArchived}
                                      title={isArchived ? 'Event is archived' : 'Download event voucher'}
                                      style={{
                                        padding: '4px 12px',
                                        backgroundColor: isArchived ? '#ccc' : '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: 4,
                                        cursor: isArchived ? 'not-allowed' : 'pointer',
                                        fontSize: '12px'
                                      }}
                                    >
                                      ⬇️ Event Voucher
                                    </button>
                                    <button
                                      onClick={() => generate500Voucher(ev, client)}
                                      disabled={isArchived}
                                      title={isArchived ? 'Event is archived' : 'Download $500 off voucher'}
                                      style={{
                                        padding: '4px 12px',
                                        backgroundColor: isArchived ? '#ccc' : '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: 4,
                                        cursor: isArchived ? 'not-allowed' : 'pointer',
                                        fontSize: '12px'
                                      }}
                                    >
                                      ⬇️ $500 Off Voucher
                                    </button>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>


{/* Facebook Ads Dropdown */}
                <div className="dropdown-section">
  <button 
    className="dropdown-header"
    onClick={() => toggleDropdown('facebookAds')}
  >
    <span>Facebook Ads</span>
    {activeDropdown === 'facebookAds' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
  </button>
  {activeDropdown === 'facebookAds' && (
  <div className="dropdown-content">
    <div style={{ 
      backgroundColor: '#e3f2fd', 
      border: '1px solid #2196f3',
      padding: '15px', 
      borderRadius: '6px', 
      marginBottom: '20px'
    }}>
      <h5 style={{ margin: '0 0 15px 0', color: '#1976d2' }}>Choose Facebook Ad</h5>
      
      {!client.manufacturer && (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '15px',
          color: '#856404'
        }}>
          <strong>Note:</strong> Please set the client manufacturer first to generate ads.
        </div>
      )}

      {/* MISSING SEARCH BAR - ADD THIS */}
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          Search Ads
        </label>
        <h6 style={{ margin: '0 0 15px 0', color: '#1976d2' }}>Double Click the Ad You Want When Searching</h6>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by ad name, month, or year..."
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
        {searchQuery && (
          <div style={{ 
            fontSize: '12px', 
            color: '#666', 
            marginTop: '4px' 
          }}>
            Found {filteredAds.length} ad{filteredAds.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* MISSING SEARCH RESULTS - ADD THIS */}
      {searchQuery && (
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Search Results
          </label>
          <div style={{ 
            maxHeight: '200px', 
            overflowY: 'auto',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: '#f8f9fa'
          }}>
            {filteredAds.length === 0 ? (
              <div style={{ 
                padding: '10px', 
                textAlign: 'center', 
                color: '#666',
                fontSize: '14px' 
              }}>
                No ads found matching "{searchQuery}"
              </div>
            ) : (
              filteredAds.map((ad, index) => (
                <div
                  key={`${ad.fullPath}-${index}`}
                  onClick={() => handleAdSelection(ad)}
                  style={{
                    padding: '8px 12px',
                    borderBottom: index < filteredAds.length - 1 ? '1px solid #dee2e6' : 'none',
                    cursor: 'pointer',
                    backgroundColor: 
                      selectedYear === ad.year && 
                      selectedMonth === ad.month && 
                      selectedAdType === ad.name ? '#007bff' : 'transparent',
                    color: 
                      selectedYear === ad.year && 
                      selectedMonth === ad.month && 
                      selectedAdType === ad.name ? 'white' : 'inherit'
                  }}
                  onMouseEnter={(e) => {
                    if (!(selectedYear === ad.year && selectedMonth === ad.month && selectedAdType === ad.name)) {
                      e.target.style.backgroundColor = '#e9ecef';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!(selectedYear === ad.year && selectedMonth === ad.month && selectedAdType === ad.name)) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div style={{ fontWeight: '500', fontSize: '14px' }}>
                    {ad.displayName}
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    opacity: 0.8,
                    color: selectedYear === ad.year && 
                          selectedMonth === ad.month && 
                          selectedAdType === ad.name ? 'rgba(255,255,255,0.8)' : '#666'
                  }}>
                    {ad.month} {ad.year}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
      
      {/* Traditional Dropdowns (show when not searching) - WRAP IN CONDITIONAL */}
      {!searchQuery && (
        <>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Year
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            >
              <option value="">Select Year</option>
              {getAvailableYears().map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Month
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              disabled={!selectedYear}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            >
              <option value="">Select Month</option>
              <option value="January">January</option>
              <option value="February">February</option>
              <option value="March">March</option>
              <option value="April">April</option>
              <option value="May">May</option>
              <option value="June">June</option>
              <option value="July">July</option>
              <option value="August">August</option>
              <option value="September">September</option>
              <option value="October">October</option>
              <option value="November">November</option>
              <option value="December">December</option>
            </select>
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Ad Type
            </label>
            <select
              value={selectedAdType}
              onChange={(e) => setSelectedAdType(e.target.value)}
              disabled={!selectedYear || !selectedMonth || loadingAdTypes}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            >
              <option value="">
                {loadingAdTypes ? 'Loading ad types...' : 'Select Ad Type'}
              </option>
              {availableAdTypes.map(adType => (
                <option key={adType.name} value={adType.name}>
                  {adType.displayName}
                </option>
              ))}
            </select>
            
            {availableAdTypes.length === 0 && selectedYear && selectedMonth && !loadingAdTypes && (
              <div style={{
                marginTop: '8px',
                padding: '8px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '4px',
                fontSize: '12px',
                color: '#856404'
              }}>
                No templates found for {client.manufacturer || 'this manufacturer'} in {selectedMonth} {selectedYear}.
              </div>
            )}
          </div>
        </>
      )}

      {/* Current Selection Display - ADD THIS */}
      {(selectedYear && selectedMonth && selectedAdType) && (
        <div style={{
          marginBottom: '15px',
          padding: '10px',
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          fontSize: '14px'
        }}>
          <strong>Selected:</strong> {selectedAdType.split(/(?=[A-Z])/).join(' ')} 
          <br />
          <span style={{ fontSize: '12px', color: '#155724' }}>
            {selectedMonth} {selectedYear}
          </span>
        </div>
      )}
      
      <button
        onClick={generateFacebookAd}
        disabled={generatingFacebookAd || !selectedYear || !selectedMonth || !selectedAdType || !client.manufacturer}
        style={{
          padding: '10px 20px',
          backgroundColor: '#1976d2',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500'
        }}
      >
        {generatingFacebookAd ? 'Generating...' : 'View Facebook Ad'}
      </button>
    </div>
  </div>
)}
</div>

{/* Facebook Profile Pictures Dropdown */}
<div className="dropdown-section">
  <button 
    className="dropdown-header"
    onClick={() => toggleDropdown('profilePictures')}
  >
    <span>Facebook Profile Pictures</span>
    {activeDropdown === 'profilePictures' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
  </button>
  {activeDropdown === 'profilePictures' && (
    <div className="dropdown-content">
      <div style={{ 
        backgroundColor: '#e3f2fd', 
        border: '1px solid #2196f3',
        padding: '15px', 
        borderRadius: '6px', 
        marginBottom: '20px'
      }}>
        <h5 style={{ margin: '0 0 15px 0', color: '#1976d2' }}>Choose Profile Picture</h5>
        
        {!client.manufacturer && (
          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            padding: '10px',
            borderRadius: '4px',
            marginBottom: '15px',
            color: '#856404'
          }}>
            <strong>Note:</strong> Please set the client manufacturer first to generate profile pictures.
          </div>
        )}

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Search Profile Pictures
          </label>
          <h6 style={{ margin: '0 0 15px 0', color: '#1976d2' }}>Double Click the Profile Picture You Want When Searching</h6>
          <input
            type="text"
            value={searchProfileQuery}
            onChange={(e) => setSearchProfileQuery(e.target.value)}
            placeholder="Search by name, month, or year..."
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
          {searchProfileQuery && (
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px' 
            }}>
              Found {filteredProfiles.length} profile picture{filteredProfiles.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        {searchProfileQuery && (
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Search Results
            </label>
            <div style={{ 
              maxHeight: '200px', 
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: '#f8f9fa'
            }}>
              {filteredProfiles.length === 0 ? (
                <div style={{ 
                  padding: '10px', 
                  textAlign: 'center', 
                  color: '#666',
                  fontSize: '14px' 
                }}>
                  No profile pictures found matching "{searchProfileQuery}"
                </div>
              ) : (
                filteredProfiles.map((profile, index) => (
                  <div
                    key={`${profile.fullPath}-${index}`}
                    onClick={() => handleProfileSelection(profile)}
                    style={{
                      padding: '8px 12px',
                      borderBottom: index < filteredProfiles.length - 1 ? '1px solid #dee2e6' : 'none',
                      cursor: 'pointer',
                      backgroundColor: 
                        selectedProfileYear === profile.year && 
                        selectedProfileMonth === profile.month && 
                        selectedProfileType === profile.name ? '#007bff' : 'transparent',
                      color: 
                        selectedProfileYear === profile.year && 
                        selectedProfileMonth === profile.month && 
                        selectedProfileType === profile.name ? 'white' : 'inherit'
                    }}
                    onMouseEnter={(e) => {
                      if (!(selectedProfileYear === profile.year && selectedProfileMonth === profile.month && selectedProfileType === profile.name)) {
                        e.target.style.backgroundColor = '#e9ecef';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!(selectedProfileYear === profile.year && selectedProfileMonth === profile.month && selectedProfileType === profile.name)) {
                        e.target.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <div style={{ fontWeight: '500', fontSize: '14px' }}>
                      {profile.displayName}
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      opacity: 0.8,
                      color: selectedProfileYear === profile.year && 
                            selectedProfileMonth === profile.month && 
                            selectedProfileType === profile.name ? 'rgba(255,255,255,0.8)' : '#666'
                    }}>
                      {profile.month} {profile.year}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        
        {!searchProfileQuery && (
          <>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Year
              </label>
              <select
                value={selectedProfileYear}
                onChange={(e) => setSelectedProfileYear(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="">Select Year</option>
                {getAvailableYears().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Month
              </label>
              <select
                value={selectedProfileMonth}
                onChange={(e) => setSelectedProfileMonth(e.target.value)}
                disabled={!selectedProfileYear}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="">Select Month</option>
                <option value="January">January</option>
                <option value="February">February</option>
                <option value="March">March</option>
                <option value="April">April</option>
                <option value="May">May</option>
                <option value="June">June</option>
                <option value="July">July</option>
                <option value="August">August</option>
                <option value="September">September</option>
                <option value="October">October</option>
                <option value="November">November</option>
                <option value="December">December</option>
              </select>
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Profile Picture Type
              </label>
              <select
                value={selectedProfileType}
                onChange={(e) => setSelectedProfileType(e.target.value)}
                disabled={!selectedProfileYear || !selectedProfileMonth || loadingProfileTypes}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="">
                  {loadingProfileTypes ? 'Loading profile types...' : 'Select Profile Picture Type'}
                </option>
                {availableProfileTypes.map(profileType => (
                  <option key={profileType.name} value={profileType.name}>
                    {profileType.displayName}
                  </option>
                ))}
              </select>
              
              {availableProfileTypes.length === 0 && selectedProfileYear && selectedProfileMonth && !loadingProfileTypes && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#856404'
                }}>
                  No templates found for {client.manufacturer || 'this manufacturer'} in {selectedProfileMonth} {selectedProfileYear}.
                </div>
              )}
            </div>
          </>
        )}

        {(selectedProfileYear && selectedProfileMonth && selectedProfileType) && (
          <div style={{
            marginBottom: '15px',
            padding: '10px',
            backgroundColor: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            fontSize: '14px'
          }}>
            <strong>Selected:</strong> {selectedProfileType.split(/(?=[A-Z])/).join(' ')} 
            <br />
            <span style={{ fontSize: '12px', color: '#155724' }}>
              {selectedProfileMonth} {selectedProfileYear}
            </span>
          </div>
        )}
        
        <button
          onClick={generateProfilePicture}
          disabled={generatingProfilePicture || !selectedProfileYear || !selectedProfileMonth || !selectedProfileType || !client.manufacturer}
          style={{
            padding: '10px 20px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {generatingProfilePicture ? 'Generating...' : 'View Profile Picture'}
        </button>
      </div>
    </div>
  )}
</div>

{/* Facebook Headers Dropdown */}
<div className="dropdown-section">
  <button 
    className="dropdown-header"
    onClick={() => toggleDropdown('headers')}
  >
    <span>Facebook Headers</span>
    {activeDropdown === 'headers' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
  </button>
  {activeDropdown === 'headers' && (
    <div className="dropdown-content">
      <div style={{ 
        backgroundColor: '#e3f2fd', 
        border: '1px solid #2196f3',
        padding: '15px', 
        borderRadius: '6px', 
        marginBottom: '20px'
      }}>
        <h5 style={{ margin: '0 0 15px 0', color: '#1976d2' }}>Choose Header</h5>
        
        {!client.manufacturer && (
          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            padding: '10px',
            borderRadius: '4px',
            marginBottom: '15px',
            color: '#856404'
          }}>
            <strong>Note:</strong> Please set the client manufacturer first to generate headers.
          </div>
        )}

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Search Headers
          </label>
          <h6 style={{ margin: '0 0 15px 0', color: '#1976d2' }}>Double Click the Header You Want When Searching</h6>
          <input
            type="text"
            value={searchHeaderQuery}
            onChange={(e) => setSearchHeaderQuery(e.target.value)}
            placeholder="Search by name, month, or year..."
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
          {searchHeaderQuery && (
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px' 
            }}>
              Found {filteredHeaders.length} header{filteredHeaders.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        {searchHeaderQuery && (
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Search Results
            </label>
            <div style={{ 
              maxHeight: '200px', 
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: '#f8f9fa'
            }}>
              {filteredHeaders.length === 0 ? (
                <div style={{ 
                  padding: '10px', 
                  textAlign: 'center', 
                  color: '#666',
                  fontSize: '14px' 
                }}>
                  No headers found matching "{searchHeaderQuery}"
                </div>
              ) : (
                filteredHeaders.map((header, index) => (
                  <div
                    key={`${header.fullPath}-${index}`}
                    onClick={() => handleHeaderSelection(header)}
                    style={{
                      padding: '8px 12px',
                      borderBottom: index < filteredHeaders.length - 1 ? '1px solid #dee2e6' : 'none',
                      cursor: 'pointer',
                      backgroundColor: 
                        selectedHeaderYear === header.year && 
                        selectedHeaderMonth === header.month && 
                        selectedHeaderType === header.name ? '#007bff' : 'transparent',
                      color: 
                        selectedHeaderYear === header.year && 
                        selectedHeaderMonth === header.month && 
                        selectedHeaderType === header.name ? 'white' : 'inherit'
                    }}
                    onMouseEnter={(e) => {
                      if (!(selectedHeaderYear === header.year && selectedHeaderMonth === header.month && selectedHeaderType === header.name)) {
                        e.target.style.backgroundColor = '#e9ecef';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!(selectedHeaderYear === header.year && selectedHeaderMonth === header.month && selectedHeaderType === header.name)) {
                        e.target.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <div style={{ fontWeight: '500', fontSize: '14px' }}>
                      {header.displayName}
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      opacity: 0.8,
                      color: selectedHeaderYear === header.year && 
                            selectedHeaderMonth === header.month && 
                            selectedHeaderType === header.name ? 'rgba(255,255,255,0.8)' : '#666'
                    }}>
                      {header.month} {header.year}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        
        {!searchHeaderQuery && (
          <>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Year
              </label>
              <select
                value={selectedHeaderYear}
                onChange={(e) => setSelectedHeaderYear(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="">Select Year</option>
                {getAvailableYears().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Month
              </label>
              <select
                value={selectedHeaderMonth}
                onChange={(e) => setSelectedHeaderMonth(e.target.value)}
                disabled={!selectedHeaderYear}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="">Select Month</option>
                <option value="January">January</option>
                <option value="February">February</option>
                <option value="March">March</option>
                <option value="April">April</option>
                <option value="May">May</option>
                <option value="June">June</option>
                <option value="July">July</option>
                <option value="August">August</option>
                <option value="September">September</option>
                <option value="October">October</option>
                <option value="November">November</option>
                <option value="December">December</option>
              </select>
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Header Type
              </label>
              <select
                value={selectedHeaderType}
                onChange={(e) => setSelectedHeaderType(e.target.value)}
                disabled={!selectedHeaderYear || !selectedHeaderMonth || loadingHeaderTypes}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="">
                  {loadingHeaderTypes ? 'Loading header types...' : 'Select Header Type'}
                </option>
                {availableHeaderTypes.map(headerType => (
                  <option key={headerType.name} value={headerType.name}>
                    {headerType.displayName}
                  </option>
                ))}
              </select>
              
              {availableHeaderTypes.length === 0 && selectedHeaderYear && selectedHeaderMonth && !loadingHeaderTypes && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#856404'
                }}>
                  No templates found for {client.manufacturer || 'this manufacturer'} in {selectedHeaderMonth} {selectedHeaderYear}.
                </div>
              )}
            </div>
          </>
        )}

        {(selectedHeaderYear && selectedHeaderMonth && selectedHeaderType) && (
          <div style={{
            marginBottom: '15px',
            padding: '10px',
            backgroundColor: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            fontSize: '14px'
          }}>
            <strong>Selected:</strong> {selectedHeaderType.split(/(?=[A-Z])/).join(' ')} 
            <br />
            <span style={{ fontSize: '12px', color: '#155724' }}>
              {selectedHeaderMonth} {selectedHeaderYear}
            </span>
          </div>
        )}
        
        <button
          onClick={generateHeader}
          disabled={generatingHeader || !selectedHeaderYear || !selectedHeaderMonth || !selectedHeaderType || !client.manufacturer}
          style={{
            padding: '10px 20px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {generatingHeader ? 'Generating...' : 'View Header'}
        </button>
      </div>
    </div>
  )}
</div>

          {/* Analytics Dropdown */}
          <div className="dropdown-section">
            <button 
              className="dropdown-header"
              onClick={() => toggleDropdown('analytics')}
            >
              <span>Analytics</span>
              {activeDropdown === 'analytics' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {activeDropdown === 'analytics' && (
              <div className="dropdown-content">
                <div style={{ 
                  backgroundColor: '#fff3cd', 
                  border: '1px solid #ffeaa7',
                  padding: '10px 15px', 
                  borderRadius: '6px', 
                  marginBottom: '15px',
                  fontSize: '14px',
                  color: '#856404'
                }}>
                  <strong>Note:</strong> Please allow up to a minute for propagation.
                </div>
                <AnalyticsTab
                  dealerAppsCode={client.dealerAppsCode}
                  clientName={client.name}
                  clientId={id}
                />
              </div>
            )}
          </div>
        </div>
      </div>



{/* Enhanced Verbiage Modal with Editing */}
{verbiageModal.open && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  }}>
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '24px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      width: '600px',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        borderBottom: '1px solid #e9ecef',
        paddingBottom: '12px'
      }}>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
          {verbiageModal.title}
          {verbiageModal.content !== verbiageModal.originalContent && !verbiageModal.isEditing && (
            <span style={{ 
              marginLeft: '8px', 
              fontSize: '12px', 
              color: '#28a745', 
              fontWeight: 'normal' 
            }}>
              (Modified)
            </span>
          )}
        </h3>
        <button
          onClick={() => setVerbiageModal({ open: false, content: '', title: '', templateName: '', isEditing: false, originalContent: '', rawTemplateContent: '' })}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666',
            padding: '0',
            width: '30px',
            height: '30px',
            borderRadius: '4px'
          }}
        >
          ×
        </button>
      </div>
      
      {/* Bold Button - Only show when editing */}
      {verbiageModal.isEditing && (
  <div style={{ marginBottom: '12px' }}>
    <button
      onClick={applyBoldToSelection}
      style={{
        padding: '6px 12px',
        backgroundColor: '#6f42c1',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: 'bold',
        marginRight: '8px'
      }}
    >
      Bold Selected Text
    </button>

    <button
      onClick={applySuperscriptToSelection}
      style={{
        padding: '6px 12px',
        backgroundColor: '#17a2b8',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: 'bold',
        marginRight: '8px'
      }}
    >
      X² Superscript
    </button>
    
    <span style={{ 
      marginLeft: '8px', 
      fontSize: '12px', 
      color: '#666' 
    }}>
      Highlight text and click "Bold Selected Text" to make it bold
    </span>
  </div>
)}
      
      {/* Content Display/Edit Area */}
      <div style={{ flex: 1, marginBottom: '16px' }}>
        {verbiageModal.isEditing ? (
          <textarea
            id="verbiage-textarea"
            value={verbiageModal.content}
            onChange={(e) => handleTextChange(e.target.value)}
            style={{
              width: '100%',
              height: '400px',
              padding: '12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '14px',
              lineHeight: '1.4',
              resize: 'vertical'
            }}
          />
        ) : (
<div style={{
  width: '100%',
  height: '400px',
  padding: '12px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  backgroundColor: '#f8f9fa',
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  fontSize: '14px',
  lineHeight: '1.4'
}}>
  {verbiageModal.content.split(/(\*\*[^*]*\*\*)/g).map((part, index) => {
    if (part.match(/^\*\*.*\*\*$/)) {
      // This is bold text - remove the ** markers and make it bold
      return (
        <strong key={index}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={index}>{part}</span>;
  })}
</div>
        )}
      </div>
      
      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        flexWrap: 'wrap'
      }}>
        {verbiageModal.isEditing ? (
          // Editing Mode Buttons
          <>
            <button
              onClick={saveEditedVerbiage}
              disabled={saving}
              style={{
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {saving ? 'Saving...' : 'Save as Custom'}
            </button>
            <button
              onClick={cancelEditingVerbiage}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Cancel
            </button>
          </>
        ) : (
          // View Mode Buttons
          <>
            <button
              onClick={startEditingVerbiage}
              style={{
                padding: '8px 16px',
                backgroundColor: '#ffc107',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Edit
            </button>
            <button
              onClick={copyToClipboard}
              style={{
                padding: '8px 16px',
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Copy to Clipboard
            </button>
          </>
        )}
        
        <button
          onClick={() => setVerbiageModal({ open: false, content: '', title: '', templateName: '', isEditing: false, originalContent: '', rawTemplateContent: '' })}
          style={{
            padding: '8px 16px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Close
        </button>
      </div>
      
      {/* Help Text */}
      {verbiageModal.isEditing && (
        <div style={{
          marginTop: '12px',
          fontSize: '12px',
          color: '#666',
          backgroundColor: '#fff3cd',
          padding: '8px',
          borderRadius: '4px',
          border: '1px solid #ffeaa7'
        }}>
          <strong>Note:</strong> Editing this verbiage will create a new custom template called "{verbiageModal.templateName} (Custom)" that you can reuse later.
        </div>
      )}
    </div>
  </div>
)}

{/* Custom Template Modal */}
{customTemplateModal.open && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  }}>
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '24px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      width: '600px',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        borderBottom: '1px solid #e9ecef',
        paddingBottom: '12px'
      }}>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
          {customTemplateModal.isEditing ? 'Edit Custom Template' : 'Create Custom Template'}
        </h3>
        <button
          onClick={() => setCustomTemplateModal({
            open: false,
            templateName: '',
            content: '',
            isEditing: false,
            originalTemplateName: null
          })}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666',
            padding: '0',
            width: '30px',
            height: '30px',
            borderRadius: '4px'
          }}
        >
          ×
        </button>
      </div>
      
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
          Template Name
        </label>
        <input
          type="text"
          value={customTemplateModal.templateName}
          onChange={(e) => setCustomTemplateModal(prev => ({
            ...prev,
            templateName: e.target.value
          }))}
          placeholder="Enter template name..."
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
      </div>
      
      <div style={{ flex: 1, marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
          Template Content
        </label>
        <textarea
          value={customTemplateModal.content}
          onChange={(e) => setCustomTemplateModal(prev => ({
            ...prev,
            content: e.target.value
          }))}
          placeholder="Enter template content... Use <<DEALERSHIP_NAME>>, <<Address>>, <<Address2>>, and <<Disclaimer>> as placeholders"
          style={{
            width: '100%',
            height: '300px',
            padding: '12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '14px',
            lineHeight: '1.4',
            resize: 'vertical'
          }}
        />
        <div style={{
          marginTop: '8px',
          fontSize: '12px',
          color: '#666',
          backgroundColor: '#f8f9fa',
          padding: '8px',
          borderRadius: '4px'
        }}>
          <strong>Available placeholders:</strong><br/>
          • <code>&lt;&lt;DEALERSHIP_NAME&gt;&gt;</code> - Client name (bold)<br/>
          • <code>&lt;&lt;Address&gt;&gt;</code> - Street address (bold)<br/>
          • <code>&lt;&lt;Address2&gt;&gt;</code> - City, State ZIP (bold)<br/>
          • <code>&lt;&lt;Disclaimer&gt;&gt;</code> - Disclaimer link
        </div>
      </div>
      
      <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end'
      }}>
        <button
          onClick={saveCustomTemplate}
          disabled={savingCustomTemplate || !customTemplateModal.templateName.trim() || !customTemplateModal.content.trim()}
          style={{
            padding: '8px 16px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {savingCustomTemplate ? 'Saving...' : 'Save Template'}
        </button>
        <button
          onClick={() => setCustomTemplateModal({
            open: false,
            templateName: '',
            content: '',
            isEditing: false,
            originalTemplateName: null
          })}
          style={{
            padding: '8px 16px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}



      {/* Disclaimer Modal */}
{/* Disclaimer Modal */}
{disclaimerModal.open && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  }}>
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '24px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      width: '800px',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        borderBottom: '1px solid #e9ecef',
        paddingBottom: '12px'
      }}>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
          {disclaimerModal.title}
        </h3>
        <button
          onClick={() => setDisclaimerModal({ open: false, imageUrl: '', title: '', type: '', bitlyLink: '' })}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666',
            padding: '0',
            width: '30px',
            height: '30px',
            borderRadius: '4px'
          }}
        >
          ×
        </button>
      </div>
      
      {/* ADD THIS MISSING IMAGE DISPLAY SECTION */}
      <div style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: '16px',
        maxHeight: '60vh',
        overflow: 'auto'
      }}>
        <img
          src={disclaimerModal.imageUrl}
          alt="Generated Disclaimer"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}
        />
      </div>
      
      <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        flexWrap: 'wrap'
      }}>
        {disclaimerModal.bitlyLink && (
          <div style={{
            flex: '1 1 100%',
            marginBottom: '12px',
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontWeight: '500' }}>Bitly Link:</span>
            <input
              type="text"
              value={disclaimerModal.bitlyLink}
              readOnly
              style={{
                flex: 1,
                padding: '4px 8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white'
              }}
            />
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(`https://${disclaimerModal.bitlyLink}`);
              alert('Link copied to clipboard!');
            } catch (err) {
              console.error('Failed to copy:', err);
            }
          }}
          style={{
            padding: '6px 12px',
            backgroundColor: '#17a2b8',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Copy Link
        </button>
      </div>
    )}
    
    {disclaimerModal.pendingGeneration && !disclaimerModal.bitlyLink && (
      <button
        onClick={generateDisclaimerLink}
        disabled={generatingDisclaimer}
        style={{
          padding: '8px 16px',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500'
        }}
      >
        {generatingDisclaimer ? 'Generating Link...' : 'Generate & Save Link'}
      </button>
    )}
    
    <button
      onClick={downloadDisclaimer}
      style={{
        padding: '8px 16px',
        backgroundColor: '#28a745',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500'
      }}
    >
      Download PNG
    </button>
    
    <button
      onClick={() => setDisclaimerModal({ 
        open: false, 
        imageUrl: '', 
        title: '', 
        type: '', 
        bitlyLink: '',
        pendingGeneration: false 
      })}
      style={{
        padding: '8px 16px',
        backgroundColor: '#6c757d',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500'
      }}
    >
      Close
    </button>
      </div>
    </div>
  </div>
)}

{facebookAdsModal.open && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 1, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  }}>
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '24px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      width: '800px',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        borderBottom: '1px solid #e9ecef',
        paddingBottom: '12px'
      }}>
        <button
          onClick={() => setFacebookAdsModal({ open: false, imageUrl: '', title: '', adName: '', year: '', month: '', adType: '' })}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666',
            padding: '0',
            width: '30px',
            height: '30px',
            borderRadius: '4px'
          }}
        >
          ×
        </button>
      </div>
      
      <div style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: '16px',
        maxHeight: '60vh',
        overflow: 'auto'
      }}>
        <img
          src={facebookAdsModal.imageUrl}
          alt="Generated Facebook Ad"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}
        />
      </div>
      
      
      <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={downloadFacebookAd}
          style={{
            padding: '8px 16px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Download Ad
        </button>
        
        <button
          onClick={() => setFacebookAdsModal({ open: false, imageUrl: '', title: '', adName: '', year: '', month: '', adType: '' })}
          style={{
            padding: '8px 16px',
            backgroundColor: '#666666ff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}

{/* Profile Picture Modal */}
{profilePictureModal.open && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 1, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  }}>
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '24px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      width: '800px',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        borderBottom: '1px solid #e9ecef',
        paddingBottom: '12px'
      }}>
        <button
          onClick={() => setProfilePictureModal({ open: false, imageUrl: '', title: '', adName: '', year: '', month: '', adType: '' })}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666',
            padding: '0',
            width: '30px',
            height: '30px',
            borderRadius: '4px'
          }}
        >
          ×
        </button>
      </div>
      
      <div style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: '16px',
        maxHeight: '60vh',
        overflow: 'auto'
      }}>
        <img
          src={profilePictureModal.imageUrl}
          alt="Generated Facebook Profile"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}
        />
      </div>
      
      
      <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={downloadProfilePicture}
          style={{
            padding: '8px 16px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Download Profile Picture
        </button>
        
        <button
          onClick={() => setProfilePictureModal({ open: false, imageUrl: '', title: '', adName: '', year: '', month: '', adType: '' })}
          style={{
            padding: '8px 16px',
            backgroundColor: '#666666ff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}

{/* Header Modal */}
{headerModal.open && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 1, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  }}>
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '24px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      width: '900px',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        borderBottom: '1px solid #e9ecef',
        paddingBottom: '12px'
      }}>
        <button
          onClick={() => setHeaderModal({ open: false, imageUrl: '', title: '', adName: '', year: '', month: '', adType: '' })}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666',
            padding: '0',
            width: '30px',
            height: '30px',
            borderRadius: '4px'
          }}
        >
          ×
        </button>
      </div>
      
      <div style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: '16px',
        maxHeight: '60vh',
        overflow: 'auto'
      }}>
        <img
          src={headerModal.imageUrl}
          alt="Generated Header"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}
        />
      </div>
      
      
      <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={downloadHeader}
          style={{
            padding: '8px 16px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Download Header
        </button>
        
        <button
          onClick={() => setHeaderModal({ open: false, imageUrl: '', title: '', adName: '', year: '', month: '', adType: '' })}
          style={{
            padding: '8px 16px',
            backgroundColor: '#666666ff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}

{/* Pre-Sale Email Modal - pick the event, then vouchers download and the email opens */}
{preSaleEmailModal.open && (() => {
  const selectedEv = events.find(e => e.id === preSaleEmailModal.eventId);
  return (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  }}>
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '24px',
      maxWidth: '90vw',
      width: '500px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '1px solid #e9ecef'
      }}>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
          Send Pre-Sale Email
        </h3>
        <button
          onClick={closePreSaleEmailModal}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666',
            padding: '0',
            width: '30px',
            height: '30px',
            borderRadius: '4px'
          }}
        >
          ×
        </button>
      </div>

      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
        Facebook Event
      </label>
      <select
        value={preSaleEmailModal.eventId}
        onChange={(e) => setPreSaleEmailModal(prev => ({ ...prev, eventId: e.target.value }))}
        style={{
          width: '100%',
          padding: '8px',
          border: '1px solid #ddd',
          borderRadius: '4px',
          marginBottom: '12px'
        }}
      >
        {preSaleEvents.map(ev => (
          <option key={ev.id} value={ev.id}>
            {ev.jobName || ev.id} ({formatShortDate(ev.startDate)} - {formatShortDate(ev.endDate)})
          </option>
        ))}
      </select>

      {selectedEv && (
        <div style={{
          padding: '10px',
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          fontSize: '14px',
          marginBottom: '12px'
        }}>
          <strong>Event Dates:</strong> {formatShortDate(selectedEv.startDate)} - {formatShortDate(selectedEv.endDate)}
        </div>
      )}

      <div style={{
        fontSize: '12px',
        color: '#856404',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffeaa7',
        padding: '8px',
        borderRadius: '4px',
        marginBottom: '16px'
      }}>
        Both vouchers (Event + $500 Off) will download and the formatted email body is copied to your clipboard. When your email program opens, click in the body and press <strong>Ctrl+V</strong>, then drag in the vouchers.
      </div>

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <button
          onClick={closePreSaleEmailModal}
          style={{
            padding: '8px 16px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSendPreSaleEmail}
          disabled={!selectedEv}
          style={{
            padding: '8px 16px',
            backgroundColor: selectedEv ? '#28a745' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedEv ? 'pointer' : 'not-allowed',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Download Vouchers & Open Email
        </button>
      </div>
    </div>
  </div>
  );
})()}

    </div>
  );
}

export default ClientProfile;